import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'fr' | 'en';
export type Country = 'CD' | 'CI' | 'GH' | 'SN' | 'ML' | 'KE' | 'UG' | 'TZ' | 'RW' | 'BF';

export interface CountryConfig {
  code: Country;
  name: string;
  nameFr: string;
  currencies: string[];
  primaryCurrency: string;
  operators: string[];
  timezone: string;
  phoneFormat: string;
  phonePrefix: string;
  phoneDigits: number; // total digits including prefix (e.g., 243XXXXXXXXX = 12)
  flag: string;
}

export interface Translations {
  [key: string]: {
    fr: string;
    en: string;
  };
}

export const countryConfigs: Record<Country, CountryConfig> = {
  'CD': {
    code: 'CD',
    name: 'Democratic Republic of Congo',
    nameFr: 'République Démocratique du Congo',
    currencies: ['CDF', 'USD'],
    primaryCurrency: 'CDF',
    operators: ['Airtel', 'Orange', 'M-Pesa', 'Afrimoney'],
    timezone: 'Africa/Kinshasa',
    phoneFormat: '+243 XX XXX XXXX',
    phonePrefix: '243',
    phoneDigits: 12,
    flag: '🇨🇩'
  },
  'CI': {
    code: 'CI',
    name: 'Côte d\'Ivoire',
    nameFr: 'Côte d\'Ivoire',
    currencies: ['XOF', 'USD', 'EUR'],
    primaryCurrency: 'XOF',
    operators: ['Orange', 'MTN', 'Moov'],
    timezone: 'Africa/Abidjan',
    phoneFormat: '+225 XX XX XX XX XX',
    phonePrefix: '225',
    phoneDigits: 13,
    flag: '🇨🇮'
  },
  'GH': {
    code: 'GH',
    name: 'Ghana',
    nameFr: 'Ghana',
    currencies: ['GHS', 'USD'],
    primaryCurrency: 'GHS',
    operators: ['MTN', 'Vodafone', 'AirtelTigo'],
    timezone: 'Africa/Accra',
    phoneFormat: '+233 XX XXX XXXX',
    phonePrefix: '233',
    phoneDigits: 12,
    flag: '🇬🇭'
  },
  'SN': {
    code: 'SN',
    name: 'Senegal',
    nameFr: 'Sénégal',
    currencies: ['XOF', 'USD', 'EUR'],
    primaryCurrency: 'XOF',
    operators: ['Orange', 'Free', 'Expresso'],
    timezone: 'Africa/Dakar',
    phoneFormat: '+221 XX XXX XX XX',
    phonePrefix: '221',
    phoneDigits: 12,
    flag: '🇸🇳'
  },
  'ML': {
    code: 'ML',
    name: 'Mali',
    nameFr: 'Mali',
    currencies: ['XOF', 'USD', 'EUR'],
    primaryCurrency: 'XOF',
    operators: ['Orange', 'Malitel'],
    timezone: 'Africa/Bamako',
    phoneFormat: '+223 XX XX XX XX',
    phonePrefix: '223',
    phoneDigits: 11,
    flag: '🇲🇱'
  },
  'KE': {
    code: 'KE',
    name: 'Kenya',
    nameFr: 'Kenya',
    currencies: ['KES', 'USD'],
    primaryCurrency: 'KES',
    operators: ['Safaricom', 'Airtel', 'Telkom'],
    timezone: 'Africa/Nairobi',
    phoneFormat: '+254 XXX XXX XXX',
    phonePrefix: '254',
    phoneDigits: 12,
    flag: '🇰🇪'
  },
  'UG': {
    code: 'UG',
    name: 'Uganda',
    nameFr: 'Ouganda',
    currencies: ['UGX', 'USD'],
    primaryCurrency: 'UGX',
    operators: ['MTN', 'Airtel', 'Africell'],
    timezone: 'Africa/Kampala',
    phoneFormat: '+256 XXX XXX XXX',
    phonePrefix: '256',
    phoneDigits: 12,
    flag: '🇺🇬'
  },
  'TZ': {
    code: 'TZ',
    name: 'Tanzania',
    nameFr: 'Tanzanie',
    currencies: ['TZS', 'USD'],
    primaryCurrency: 'TZS',
    operators: ['Vodacom', 'Airtel', 'Tigo', 'Halotel'],
    timezone: 'Africa/Dar_es_Salaam',
    phoneFormat: '+255 XXX XXX XXX',
    phonePrefix: '255',
    phoneDigits: 12,
    flag: '🇹🇿'
  },
  'RW': {
    code: 'RW',
    name: 'Rwanda',
    nameFr: 'Rwanda',
    currencies: ['RWF', 'USD'],
    primaryCurrency: 'RWF',
    operators: ['MTN', 'Airtel'],
    timezone: 'Africa/Kigali',
    phoneFormat: '+250 XXX XXX XXX',
    phonePrefix: '250',
    phoneDigits: 12,
    flag: '🇷🇼'
  },
  'BF': {
    code: 'BF',
    name: 'Burkina Faso',
    nameFr: 'Burkina Faso',
    currencies: ['XOF', 'USD', 'EUR'],
    primaryCurrency: 'XOF',
    operators: ['Orange', 'Telecel'],
    timezone: 'Africa/Ouagadougou',
    phoneFormat: '+226 XX XX XX XX',
    phonePrefix: '226',
    phoneDigits: 11,
    flag: '🇧🇫'
  }
};

const translations: Translations = {
  // ── Common ──
  'common.hide': { fr: 'Masquer', en: 'Hide' },
  'common.show': { fr: 'Afficher', en: 'Show' },
  'common.hidden': { fr: 'Masqué', en: 'Hidden' },
  'common.today': { fr: "Aujourd'hui", en: 'Today' },
  'common.loading': { fr: 'Chargement...', en: 'Loading...' },
  'common.save': { fr: 'Sauvegarder', en: 'Save' },
  'common.cancel': { fr: 'Annuler', en: 'Cancel' },
  'common.delete': { fr: 'Supprimer', en: 'Delete' },
  'common.edit': { fr: 'Modifier', en: 'Edit' },
  'common.add': { fr: 'Ajouter', en: 'Add' },
  'common.search': { fr: 'Rechercher', en: 'Search' },
  'common.filter': { fr: 'Filtrer', en: 'Filter' },
  'common.export': { fr: 'Exporter', en: 'Export' },
  'common.amount': { fr: 'Montant', en: 'Amount' },
  'common.currency': { fr: 'Devise', en: 'Currency' },
  'common.date': { fr: 'Date', en: 'Date' },
  'common.status': { fr: 'Statut', en: 'Status' },
  'common.operator': { fr: 'Opérateur', en: 'Operator' },
  'common.security': { fr: 'Sécurité', en: 'Security' },
  'common.logout': { fr: 'Déconnexion', en: 'Sign Out' },
  'common.last7days': { fr: '7 derniers jours', en: 'Last 7 days' },
  'common.last30days': { fr: '30 derniers jours', en: 'Last 30 days' },
  'common.last3months': { fr: '3 derniers mois', en: 'Last 3 months' },
  'common.mobileOperator': { fr: 'Opérateur Mobile', en: 'Mobile Operator' },
  'common.refresh': { fr: 'Actualiser', en: 'Refresh' },
  'common.close': { fr: 'Fermer', en: 'Close' },
  'common.back': { fr: 'Retour', en: 'Back' },
  'common.next': { fr: 'Suivant', en: 'Next' },
  'common.confirm': { fr: 'Confirmer', en: 'Confirm' },
  'common.reset': { fr: 'Réinitialiser', en: 'Reset' },
  'common.send': { fr: 'Envoyer', en: 'Send' },
  'common.copy': { fr: 'Copier', en: 'Copy' },
  'common.download': { fr: 'Télécharger', en: 'Download' },
  'common.upload': { fr: 'Importer', en: 'Upload' },
  'common.yes': { fr: 'Oui', en: 'Yes' },
  'common.no': { fr: 'Non', en: 'No' },
  'common.all': { fr: 'Tous', en: 'All' },
  'common.none': { fr: 'Aucun', en: 'None' },
  'common.actions': { fr: 'Actions', en: 'Actions' },
  'common.details': { fr: 'Détails', en: 'Details' },
  'common.description': { fr: 'Description', en: 'Description' },
  'common.notProvided': { fr: 'Non renseigné', en: 'Not provided' },
  'common.error': { fr: 'Erreur', en: 'Error' },
  'common.success': { fr: 'Succès', en: 'Success' },
  'common.warning': { fr: 'Avertissement', en: 'Warning' },
  'common.user': { fr: 'Utilisateur', en: 'User' },
  'common.liveSystem': { fr: 'Système actif', en: 'Live System' },
  'common.comingSoon': { fr: 'Bientôt disponible', en: 'Coming Soon' },
  'common.unavailable': { fr: 'Indisponible', en: 'Unavailable' },
  'common.version': { fr: 'Version', en: 'Version' },
  'common.environment': { fr: 'Environnement', en: 'Environment' },
  'common.production': { fr: 'Production', en: 'Production' },
  'common.lastUpdate': { fr: 'Dernière mise à jour', en: 'Last update' },
  'common.saveChanges': { fr: 'Enregistrer les modifications', en: 'Save Changes' },
  'common.saving': { fr: 'Enregistrement...', en: 'Saving...' },

  // ── Navigation ──
  'nav.dashboard': { fr: 'Tableau de bord', en: 'Dashboard' },
  'nav.transfers': { fr: 'Transferts', en: 'Transfers' },
  'nav.balances': { fr: 'Balances', en: 'Balances' },
  'nav.reports': { fr: 'Rapports', en: 'Reports' },
  'nav.profile': { fr: 'Profil', en: 'Profile' },
  'nav.account': { fr: 'Mon Compte', en: 'My Account' },
  'nav.team': { fr: 'Mon Équipe', en: 'My Team' },
  'nav.settings': { fr: 'Paramètres', en: 'Settings' },
  'nav.notifications': { fr: 'Notifications', en: 'Notifications' },
  'nav.support': { fr: 'Support', en: 'Support' },
  'nav.docs': { fr: 'Documentation', en: 'Documentation' },
  'nav.withdrawal': { fr: 'Décaissement', en: 'Cashout' },
  'nav.tickets': { fr: 'Tickets', en: 'Tickets' },
  'nav.analytics': { fr: 'Analyses', en: 'Analytics' },
  'nav.cards': { fr: 'Gestion des cartes', en: 'Card Management' },
  'nav.virtualCards': { fr: 'Cartes virtuelles', en: 'Virtual Cards' },
  'nav.transactions': { fr: 'Transactions', en: 'Transactions' },
  'nav.walletHistory': { fr: 'Historique wallet', en: 'Wallet History' },
  'nav.apiKeys': { fr: 'Clés API', en: 'API Keys' },
  'nav.ipWhitelist': { fr: 'Liste blanche IP', en: 'IP Whitelist' },
  'nav.events': { fr: 'Événements', en: 'Events' },
  'nav.failureAnalysis': { fr: 'Analyse des échecs', en: 'Failure analysis' },
  'nav.lookup': { fr: 'Recherche transaction', en: 'Transaction lookup' },
  'nav.documentation': { fr: 'Documentation', en: 'Documentation' },
  'nav.cashoutSettings': { fr: 'Paramètres de décaissement', en: 'Cashout settings' },
  'nav.auditLog': { fr: 'Journal d\'audit', en: 'Audit log' },
  'nav.twofa': { fr: '2FA', en: 'Two-factor' },
  'nav.customRoles': { fr: 'Rôles personnalisés', en: 'Custom roles' },

  // ── Sidebar section headers ──
  'sidebar.section.overview': { fr: 'VUE D\'ENSEMBLE', en: 'OVERVIEW' },
  'sidebar.section.wallets': { fr: 'WALLETS', en: 'WALLETS' },
  'sidebar.section.cards': { fr: 'CARTES', en: 'CARDS' },
  'sidebar.section.finance': { fr: 'FINANCE', en: 'FINANCE' },
  'sidebar.section.analytics': { fr: 'ANALYSES', en: 'ANALYTICS' },
  'sidebar.section.management': { fr: 'GESTION', en: 'MANAGEMENT' },
  'sidebar.section.access': { fr: 'ACCÈS & SÉCURITÉ', en: 'ACCESS & SECURITY' },
  'sidebar.section.developers': { fr: 'DÉVELOPPEURS', en: 'DEVELOPERS' },
  'sidebar.section.resources': { fr: 'RESSOURCES', en: 'RESOURCES' },

  // ── Login ──
  'login.title': { fr: 'Connexion', en: 'Sign In' },
  'login.subtitle': { fr: 'Accédez à votre espace marchand', en: 'Access your merchant dashboard' },
  'login.username': { fr: 'Nom d\'utilisateur', en: 'Username' },
  'login.usernamePlaceholder': { fr: 'Entrez votre nom d\'utilisateur', en: 'Enter your username' },
  'login.email': { fr: 'Adresse email', en: 'Email address' },
  'login.password': { fr: 'Mot de passe', en: 'Password' },
  'login.passwordPlaceholder': { fr: 'Entrez votre mot de passe', en: 'Enter your password' },
  'login.signin': { fr: 'Se connecter', en: 'Sign In' },
  'login.signingIn': { fr: 'Connexion en cours...', en: 'Signing in...' },
  'login.forgotPassword': { fr: 'Mot de passe oublié ?', en: 'Forgot password?' },
  'login.demo': { fr: 'Comptes de démonstration', en: 'Demo accounts' },
  'login.errorInvalid': { fr: 'Nom d\'utilisateur ou mot de passe incorrect', en: 'Incorrect username or password' },
  'login.errorGeneric': { fr: 'Une erreur est survenue lors de la connexion', en: 'An error occurred during login' },
  'login.welcome': { fr: 'Bienvenue', en: 'Welcome' },
  'login.heroTitle': { fr: 'Gérez vos paiements en toute simplicité', en: 'Manage your payments with ease' },
  'login.heroSubtitle': { fr: 'Une plateforme complète pour suivre vos transactions, gérer vos transferts et analyser vos performances.', en: 'A complete platform to track your transactions, manage your transfers and analyze your performance.' },
  'login.feature1': { fr: 'Suivi en temps réel', en: 'Real-time tracking' },
  'login.feature2': { fr: 'Multi-opérateurs', en: 'Multi-operator' },
  'login.feature3': { fr: 'Rapports avancés', en: 'Advanced reports' },
  'login.secureAccess': { fr: 'Accès sécurisé', en: 'Secure access' },
  'login.allRightsReserved': { fr: 'Tous droits réservés', en: 'All rights reserved' },
  'login.merchantDashboard': { fr: 'Tableau de bord marchand', en: 'Merchant Dashboard' },
  'login.rememberMe': { fr: 'Rester connecte', en: 'Keep me signed in' },
  'login.encrypted': { fr: 'Chiffre SSL', en: 'SSL Encrypted' },
  'login.twofa.badge': { fr: 'Double authentification', en: 'Two-factor' },
  'login.twofa.title': { fr: 'Vérifions que c\'est bien vous', en: 'Verify it\'s you' },
  'login.twofa.subtitle': { fr: 'Saisissez le code à 6 chiffres affiché dans votre application d\'authentification.', en: 'Enter the 6-digit code from your authenticator app.' },
  'login.twofa.codeLabel': { fr: 'Code d\'authentification', en: 'Authentication code' },
  'login.twofa.backupHint': { fr: 'Un code de secours fonctionne aussi si vous avez perdu votre appareil.', en: 'A backup code also works if you have lost your device.' },
  'login.twofa.submit': { fr: 'Vérifier et se connecter', en: 'Verify and sign in' },
  'login.twofa.verifying': { fr: 'Vérification…', en: 'Verifying…' },
  'login.twofa.cancel': { fr: 'Annuler et utiliser un autre compte', en: 'Cancel and use a different account' },

  // ── Dashboard ──
  'dashboard.title': { fr: 'Tableau de bord', en: 'Dashboard' },
  'dashboard.overview': { fr: 'Vue d\'ensemble de vos activités', en: 'Overview of your activities' },
  'dashboard.dashboardOverview': { fr: 'Vue d\'ensemble', en: 'Dashboard Overview' },
  'dashboard.realtimeAnalytics': { fr: 'Analyses de performance en temps réel', en: 'Real-time performance analytics' },
  'dashboard.lastUpdate': { fr: 'Dernière mise à jour', en: 'Last update' },
  'dashboard.totalTransactions': { fr: 'Total Transactions', en: 'Total Transactions' },
  'dashboard.monthlyRevenue': { fr: 'Revenus du Mois', en: 'Monthly Revenue' },
  'dashboard.successfulTransactions': { fr: 'Transactions Réussies', en: 'Successful Transactions' },
  'dashboard.failedTransactions': { fr: 'Transactions Échouées', en: 'Failed Transactions' },
  'dashboard.transactionVolume': { fr: 'Volume des Transactions', en: 'Transaction Volume' },
  'dashboard.operatorStats': { fr: 'Statistiques par Opérateur', en: 'Statistics by Operator' },
  'dashboard.loadingError': { fr: 'Erreur de chargement', en: 'Loading Error' },
  'dashboard.tryAgain': { fr: 'Réessayer', en: 'Try Again' },
  'dashboard.live': { fr: 'EN DIRECT', en: 'LIVE' },
  'dashboard.autoRefresh': { fr: 'Actualisation auto', en: 'Auto-refresh' },
  'dashboard.refreshing': { fr: 'Actualisation...', en: 'Refreshing...' },
  'dashboard.updating': { fr: 'Mise à jour...', en: 'Updating...' },
  'dashboard.greetingMorning': { fr: 'Bonjour ! Bonne journée productive !', en: 'Good morning! Have a productive day ahead!' },
  'dashboard.greetingAfternoon': { fr: 'Bon après-midi ! Votre activité est florissante !', en: 'Good afternoon! Your business is thriving!' },
  'dashboard.greetingEvening': { fr: 'Bonsoir ! Nous vous souhaitons une bonne soirée !', en: 'Good evening! Wishing you a peaceful night!' },
  'dashboard.totalDeposits': { fr: 'Total Dépôts', en: 'Total Deposits' },
  'dashboard.totalWithdrawals': { fr: 'Total Retraits', en: 'Total Withdrawals' },
  'dashboard.netBalance': { fr: 'Solde Net', en: 'Net Balance' },
  'dashboard.deposits': { fr: 'Dépôts', en: 'Deposits' },
  'dashboard.withdrawals': { fr: 'Retraits', en: 'Withdrawals' },
  'dashboard.today': { fr: 'Aujourd\'hui', en: 'Today' },
  'dashboard.incoming': { fr: 'Entrants', en: 'Incoming' },
  'dashboard.outgoing': { fr: 'Sortants', en: 'Outgoing' },
  'dashboard.realtime': { fr: 'Temps réel', en: 'Real-time' },
  'dashboard.hourlyBreakdown': { fr: 'Répartition horaire', en: 'Hourly breakdown' },
  'dashboard.noChartData': { fr: 'Aucune donnée graphique disponible', en: 'No chart data available' },
  'dashboard.operatorDistribution': { fr: 'Répartition par Opérateur', en: 'Operator Distribution' },
  'dashboard.byTransactions': { fr: 'Par transactions', en: 'By transactions' },
  'dashboard.noData': { fr: 'Aucune donnée disponible', en: 'No data available' },
  'dashboard.balanceByOperator': { fr: 'Solde par Opérateur', en: 'Balance by Operator' },
  'dashboard.depositsVsWithdrawals': { fr: 'Dépôts vs Retraits', en: 'Deposits vs Withdrawals' },
  'dashboard.recentTransactions': { fr: 'Transactions Récentes', en: 'Recent Transactions' },
  'dashboard.latestActivity': { fr: 'Dernière activité', en: 'Latest activity' },
  'dashboard.noTransactions': { fr: 'Aucune transaction récente', en: 'No recent transactions' },

  // ── Transaction Status ──
  'status.successful': { fr: 'Réussies', en: 'Successful' },
  'status.failed': { fr: 'Échouées', en: 'Failed' },
  'status.pending': { fr: 'En attente', en: 'Pending' },
  'status.submitted': { fr: 'Soumises', en: 'Submitted' },
  'status.processing': { fr: 'En cours', en: 'Processing' },
  'status.active': { fr: 'Actif', en: 'Active' },
  'status.inactive': { fr: 'Inactif', en: 'Inactive' },
  'status.online': { fr: 'En ligne', en: 'Online' },
  'status.live': { fr: 'Actif', en: 'Live' },

  // ── Transfers ──
  'transfers.title': { fr: 'Transferts d\'argent', en: 'Money Transfers' },
  'transfers.subtitle': { fr: 'Transférez des fonds entre portefeuilles et opérateurs', en: 'Seamlessly move funds between wallets and operators' },
  'transfers.manage': { fr: 'Gérez vos transferts d\'argent', en: 'Manage your money transfers' },
  'transfers.walletToWallet': { fr: 'Wallet à Wallet', en: 'Wallet to Wallet' },
  'transfers.walletToBank': { fr: 'Wallet vers Banque', en: 'Wallet to Bank' },
  'transfers.bankToWallet': { fr: 'Banque vers Wallet', en: 'Bank to Wallet' },
  'transfers.history': { fr: 'Historique', en: 'History' },
  'transfers.intraOperator': { fr: 'Intra-Opérateur', en: 'Intra-Operator' },
  'transfers.interOperator': { fr: 'Inter-Opérateur', en: 'Inter-Operator' },
  'transfers.note': { fr: 'Note (optionnel)', en: 'Note (optional)' },
  'transfers.cardToMobile.tab': { fr: 'Carte → Mobile Money', en: 'Card → Mobile Money' },
  'transfers.cardToMobile.title': { fr: 'Transfert Carte vers Mobile Money', en: 'Card to Mobile Money Transfer' },
  'transfers.cardToMobile.subtitle': {
    fr: 'Déplacez les fonds de votre carte virtuelle vers un wallet opérateur',
    en: 'Move funds from your virtual card to a mobile money operator wallet'
  },
  'transfers.cardToMobile.sourceCard': { fr: 'Source — Carte virtuelle', en: 'Source — Virtual Card' },
  'transfers.cardToMobile.targetOperator': { fr: 'Cible — Mobile Money', en: 'Target — Mobile Money' },
  'transfers.cardToMobile.targetOperatorLabel': { fr: 'Opérateur cible', en: 'Target operator' },
  'transfers.cardToMobile.targetWallet': { fr: 'Wallet cible', en: 'Target wallet' },
  'transfers.cardToMobile.depositHint': {
    fr: 'Crédité sur le wallet Pay-in (collection)',
    en: 'Credited to Pay-in wallet (collection)'
  },
  'transfers.cardToMobile.withdrawalHint': {
    fr: 'Crédité sur le wallet Pay-out (cashout)',
    en: 'Credited to Pay-out wallet (cashout)'
  },
  'transfers.cardToMobile.configuration': { fr: 'Configuration du transfert', en: 'Transfer configuration' },
  'transfers.cardToMobile.details': { fr: 'Détails du transfert', en: 'Transfer details' },
  'transfers.cardToMobile.amount': { fr: 'Montant à transférer', en: 'Transfer amount' },
  'transfers.cardToMobile.availableOnCard': { fr: 'Disponible sur la carte', en: 'Available on card' },
  'transfers.cardToMobile.exceeds': { fr: 'Montant supérieur au solde de la carte', en: 'Amount exceeds card balance' },
  'transfers.cardToMobile.noCard': { fr: 'Aucune carte LIVE disponible pour cette devise', en: 'No LIVE card available for this currency' },
  'transfers.cardToMobile.invalidAmount': { fr: 'Le montant doit être supérieur à 0', en: 'Amount must be greater than 0' },
  'transfers.cardToMobile.summary': { fr: 'Récapitulatif du transfert', en: 'Transfer summary' },
  'transfers.cardToMobile.confirm': { fr: 'Confirmer le transfert', en: 'Confirm transfer' },
  'transfers.cardToMobile.processing': { fr: 'Traitement en cours...', en: 'Processing transfer...' },
  'transfers.cardToMobile.notePlaceholder': {
    fr: 'Ex. : Solder les recettes carte de la semaine',
    en: 'e.g. Settle weekly card receipts'
  },
  'transfers.cardToMobile.securityHint': {
    fr: 'La carte est débitée de manière atomique ; le wallet mobile money est crédité en cas de succès',
    en: 'Card is debited atomically; mobile money wallet is credited on success'
  },
  'transfers.cardToMobile.loadCardError': {
    fr: 'Impossible de charger les portefeuilles carte',
    en: 'Failed to load card wallets'
  },
  'transfers.availableTypes': { fr: 'Types disponibles', en: 'Available Types' },
  'transfers.realtime': { fr: 'Temps réel', en: 'Real-time' },
  'transfers.liveTracking': { fr: 'Suivi en direct', en: 'Live Tracking' },
  'transfers.bankTransfers': { fr: 'Transferts bancaires', en: 'Bank Transfers' },
  'transfers.featureUnavailable': { fr: 'Cette fonctionnalité est actuellement indisponible et sera bientôt disponible.', en: 'This feature is currently unavailable and will be released soon.' },
  'transfers.availableAlternatives': { fr: 'Alternatives disponibles :', en: 'Available alternatives:' },
  'transfers.useAlternatives': { fr: 'Utilisez les transferts Intra-Opérateur ou Inter-Opérateur pour déplacer des fonds entre vos portefeuilles.', en: 'Use Intra-Operator or Inter-Operator transfers to move funds between your wallets.' },
  'transfers.viewAll': { fr: 'Voir tous les transferts', en: 'View all transfers' },
  'transfers.viewDetails': { fr: 'Voir détails', en: 'View details' },

  // ── Balances ──
  'balances.title': { fr: 'Soldes des portefeuilles', en: 'Wallet Balances' },
  'balances.subtitle': { fr: 'Vue en temps réel de vos portefeuilles de paiement', en: 'Real-time overview of your payment wallets' },
  'balances.manage': { fr: 'Gérez vos soldes et portefeuilles', en: 'Manage your balances and wallets' },
  'balances.overview': { fr: 'Vue d\'ensemble', en: 'Overview' },
  'balances.operators': { fr: 'Par opérateurs', en: 'By Operators' },
  'balances.deposit': { fr: 'Dépôt', en: 'Deposit' },
  'balances.withdrawal': { fr: 'Retrait', en: 'Withdrawal' },
  'balances.total': { fr: 'Total', en: 'Total' },
  'balances.payIn': { fr: 'Pay-in :', en: 'Pay-in:' },
  'balances.payOut': { fr: 'Pay-out :', en: 'Pay-out:' },
  'balances.available': { fr: 'Disponible', en: 'Available' },
  'balances.availableHint': { fr: 'Utilisable maintenant', en: 'Usable now' },
  'balances.inFlight': { fr: 'Réservé', en: 'Reserved' },
  'balances.inFlightHint': {
    fr: 'Bloqué pour des paiements ou transferts en cours',
    en: 'Locked for pending payouts or transfers'
  },
  'balances.totalHint': { fr: 'Disponible + Réservé', en: 'Available + Reserved' },
  'balances.reserved': { fr: 'Réservé', en: 'Reserved' },
  'balances.showDetails': { fr: 'Voir le détail', en: 'Show details' },
  'balances.hideDetails': { fr: 'Masquer le détail', en: 'Hide details' },
  'balances.clickForDetails': { fr: 'Détails', en: 'Details' },
  'balances.detailsTitle': { fr: 'Détail du portefeuille', en: 'Wallet details' },
  'balances.decomposition': { fr: 'Décomposition', en: 'Breakdown' },
  'balances.decompositionHint': {
    fr: 'Pay-in + Pay-out = Total. Le Réservé est déjà inclus dans ces deux poches.',
    en: 'Pay-in + Pay-out = Total. Reserved is already included in these two buckets.'
  },
  'balances.detailsHelp': {
    fr: "« Disponible » : ce que vous pouvez utiliser tout de suite (réservations déjà soustraites). « Réservé » : montants bloqués pour des paiements clients ou des transferts internes initiés mais pas encore confirmés. Quand vous initiez un retrait, le montant passe de Disponible à Réservé ; il est définitivement débité du Total une fois la transaction confirmée, ou rendu au Disponible en cas d'échec. Les soldes entrants ne passent pas par « Réservé », ils arrivent directement dans Pay-in.",
    en: "\"Available\" is what you can use right now (reservations already deducted). \"Reserved\" is the amount locked for pending customer payouts or internal transfers that have been initiated but not yet confirmed. When you initiate a withdrawal, the amount moves from Available to Reserved; it is debited from the Total once the transaction is confirmed, or returned to Available on failure. Incoming settlements do not pass through \"Reserved\" — they land directly in Pay-in."
  },
  'balances.loading': { fr: 'Chargement des balances...', en: 'Loading balances...' },
  'balances.historyTitle': { fr: 'Historique des balances', en: 'Balance History' },
  'balances.historyUnavailable': { fr: 'L\'historique détaillé des balances et analyses sera bientôt disponible.', en: 'Detailed balance history and analytics will be available soon.' },
  'balances.currentFocus': { fr: 'Focus actuel :', en: 'Current focus:' },
  'balances.realTimeAvailable': { fr: 'Les balances en temps réel sont disponibles dans les onglets Vue d\'ensemble et Par opérateurs.', en: 'Real-time balances are available in the Overview and By Operators tabs.' },

  // ── Reports ──
  'reports.title': { fr: 'Rapports', en: 'Reports' },
  'reports.analyze': { fr: 'Analysez vos performances et générez des rapports', en: 'Analyze your performance and generate reports' },
  'reports.transactions': { fr: 'Transactions', en: 'Transactions' },
  'reports.analytics': { fr: 'Analyses', en: 'Analytics' },
  'reports.custom': { fr: 'Personnalisés', en: 'Custom' },
  'reports.scheduled': { fr: 'Programmés', en: 'Scheduled' },
  'reports.daily': { fr: 'Quotidien', en: 'Daily' },
  'reports.dailyReports': { fr: 'Rapports quotidiens', en: 'Daily Reports' },
  'reports.customReports': { fr: 'Rapports personnalisés', en: 'Custom Reports' },
  'reports.exportReports': { fr: 'Exporter les rapports', en: 'Export Reports' },
  'reports.dailyDesc': { fr: 'Visualisez et exportez les transactions quotidiennes avec un filtrage avancé', en: 'View and export daily transactions with advanced filtering' },
  'reports.customDesc': { fr: 'Recherchez des transactions par plage de dates personnalisée', en: 'Search transactions by custom date range' },
  'reports.exportDesc': { fr: 'Exportez les transactions au format Excel ou CSV', en: 'Export transactions in Excel or CSV format' },
  'reports.comprehensive': { fr: 'Rapports de transactions complets', en: 'Comprehensive transaction reports' },

  // ── Transactions ──
  'transactions.mobileDeposit': { fr: 'Dépôt mobile money', en: 'Mobile money deposit' },
  'transactions.internationalTransfer': { fr: 'Transfert international', en: 'International transfer' },
  'transactions.fundsReceived': { fr: 'Réception de fonds', en: 'Funds received' },
  'transactions.bankTransfer': { fr: 'Transfert vers banque', en: 'Bank transfer' },
  'transactions.merchantPayment': { fr: 'Paiement marchand', en: 'Merchant payment' },
  'transactions.recent': { fr: 'Transactions Récentes', en: 'Recent Transactions' },
  'transactions.yourLatest': { fr: 'Vos dernières opérations', en: 'Your latest operations' },
  'transactions.viewAll': { fr: 'Voir tout', en: 'View all' },
  'transactions.successful': { fr: 'Réussie', en: 'Successful' },
  'transactions.pending': { fr: 'En attente', en: 'Pending' },
  'transactions.failed': { fr: 'Échec', en: 'Failed' },
  'transactions.ago': { fr: 'Il y a', en: 'ago' },

  // ── Profile ──
  'profile.title': { fr: 'Profil', en: 'Profile' },
  'profile.subtitle': { fr: 'Gérez vos informations de compte et préférences', en: 'Manage your account information and preferences' },
  'profile.personalInfo': { fr: 'Informations personnelles', en: 'Personal Information' },
  'profile.firstName': { fr: 'Prénom', en: 'First Name' },
  'profile.lastName': { fr: 'Nom', en: 'Last Name' },
  'profile.email': { fr: 'Email', en: 'Email' },
  'profile.merchantCode': { fr: 'Code marchand', en: 'Merchant Code' },
  'profile.institutionInfo': { fr: 'Informations de l\'institution', en: 'Institution Information' },
  'profile.companyName': { fr: 'Nom de l\'entreprise', en: 'Company Name' },
  'profile.institutionCode': { fr: 'Code institution', en: 'Institution Code' },
  'profile.phone': { fr: 'Téléphone', en: 'Phone' },
  'profile.institutionEmail': { fr: 'Email de l\'institution', en: 'Institution Email' },
  'profile.website': { fr: 'Site web', en: 'Website' },
  'profile.address': { fr: 'Adresse', en: 'Address' },
  'profile.businessSector': { fr: 'Secteur d\'activité', en: 'Business Sector' },
  'profile.legalStatus': { fr: 'Statut juridique', en: 'Legal Status' },
  'profile.preferences': { fr: 'Préférences', en: 'Preferences' },
  'profile.timezone': { fr: 'Fuseau horaire', en: 'Timezone' },
  'profile.language': { fr: 'Langue', en: 'Language' },
  'profile.updateSuccess': { fr: 'Profil mis à jour avec succès !', en: 'Profile updated successfully!' },
  'profile.updateError': { fr: 'Erreur lors de la mise à jour du profil', en: 'Error updating profile' },
  'profile.security': { fr: 'Sécurité', en: 'Security' },
  'profile.changePassword': { fr: 'Changer le mot de passe', en: 'Change Password' },
  'profile.currentPassword': { fr: 'Mot de passe actuel', en: 'Current Password' },
  'profile.newPassword': { fr: 'Nouveau mot de passe', en: 'New Password' },
  'profile.confirmPassword': { fr: 'Confirmer le nouveau mot de passe', en: 'Confirm New Password' },
  'profile.passwordChanged': { fr: 'Mot de passe modifié avec succès', en: 'Password changed successfully' },
  'profile.passwordChangeError': { fr: 'Erreur lors du changement de mot de passe', en: 'Error changing password' },
  'profile.passwordMismatch': { fr: 'Les mots de passe ne correspondent pas', en: 'Passwords do not match' },
  'profile.passwordMinLength': { fr: 'Minimum 6 caractères', en: 'Minimum 6 characters' },
  'profile.allFieldsRequired': { fr: 'Tous les champs sont requis', en: 'All fields are required' },
  'profile.accountInfo': { fr: 'Informations du compte', en: 'Account Info' },
  'profile.role': { fr: 'Rôle', en: 'Role' },

  // ── Settings ──
  'settings.title': { fr: 'Paramètres système', en: 'System Settings' },
  'settings.subtitle': { fr: 'Configurez votre application selon vos préférences', en: 'Configure your application according to your preferences' },
  'settings.unavailable': { fr: 'Paramètres indisponibles', en: 'Settings Unavailable' },
  'settings.unavailableDesc': { fr: 'Le panneau de paramètres est en cours de développement et sera disponible dans une prochaine mise à jour. Merci de votre patience.', en: 'The settings panel is currently under development and will be available in a future update. Thank you for your patience.' },
  'settings.estimatedAvailability': { fr: 'Disponibilité estimée : Prochaine mise à jour', en: 'Estimated availability: Next platform update' },
  'settings.general': { fr: 'Général', en: 'General' },
  'settings.localization': { fr: 'Localisation', en: 'Localization' },
  'settings.notifications': { fr: 'Notifications', en: 'Notifications' },
  'settings.security': { fr: 'Sécurité', en: 'Security' },
  'settings.system': { fr: 'Système', en: 'System' },
  'settings.interface': { fr: 'Interface', en: 'Interface' },
  'settings.theme': { fr: 'Thème', en: 'Theme' },
  'settings.themeLight': { fr: 'Clair', en: 'Light' },
  'settings.themeDark': { fr: 'Sombre', en: 'Dark' },
  'settings.themeAuto': { fr: 'Auto', en: 'Auto' },
  'settings.dateFormat': { fr: 'Format de date', en: 'Date Format' },
  'settings.numberFormat': { fr: 'Format numérique', en: 'Number Format' },
  'settings.numberEuropean': { fr: 'Européen (1 234,56)', en: 'European (1 234,56)' },
  'settings.numberAmerican': { fr: 'Américain (1,234.56)', en: 'American (1,234.56)' },
  'settings.timezone': { fr: 'Fuseau horaire', en: 'Timezone' },
  'settings.defaultCurrency': { fr: 'Devise par défaut', en: 'Default Currency' },
  'settings.emailNotifications': { fr: 'Notifications par email', en: 'Email Notifications' },
  'settings.smsNotifications': { fr: 'Notifications SMS', en: 'SMS Notifications' },
  'settings.pushNotifications': { fr: 'Notifications push', en: 'Push Notifications' },
  'settings.transactionAlerts': { fr: 'Alertes de transactions', en: 'Transaction Alerts' },
  'settings.balanceAlerts': { fr: 'Alertes de solde', en: 'Balance Alerts' },
  'settings.securityAlerts': { fr: 'Alertes de sécurité', en: 'Security Alerts' },
  'settings.marketingEmails': { fr: 'Emails marketing', en: 'Marketing Emails' },
  'settings.sessionTimeout': { fr: 'Délai d\'expiration de session (minutes)', en: 'Session Timeout (minutes)' },
  'settings.twoFactor': { fr: 'Authentification à deux facteurs', en: 'Two-Factor Authentication' },
  'settings.loginAlerts': { fr: 'Alertes de connexion', en: 'Login Alerts' },
  'settings.ipWhitelist': { fr: 'Liste blanche IP', en: 'IP Whitelist' },
  'settings.autoLogout': { fr: 'Déconnexion automatique', en: 'Auto Logout' },
  'settings.backupFrequency': { fr: 'Fréquence de sauvegarde', en: 'Backup Frequency' },
  'settings.hourly': { fr: 'Toutes les heures', en: 'Hourly' },
  'settings.daily': { fr: 'Quotidien', en: 'Daily' },
  'settings.weekly': { fr: 'Hebdomadaire', en: 'Weekly' },
  'settings.monthly': { fr: 'Mensuel', en: 'Monthly' },
  'settings.dataRetention': { fr: 'Rétention des données (jours)', en: 'Data Retention (days)' },
  'settings.apiRateLimit': { fr: 'Limite API (requêtes/heure)', en: 'API Rate Limit (requests/hour)' },
  'settings.autoBackup': { fr: 'Sauvegarde automatique', en: 'Auto Backup' },
  'settings.maintenanceMode': { fr: 'Mode maintenance', en: 'Maintenance Mode' },
  'settings.debugMode': { fr: 'Mode débogage', en: 'Debug Mode' },
  'settings.itemsPerPage': { fr: 'Éléments par page', en: 'Items Per Page' },
  'settings.compactMode': { fr: 'Mode compact', en: 'Compact Mode' },
  'settings.showBalances': { fr: 'Afficher les soldes par défaut', en: 'Show Balances by Default' },
  'settings.chartAnimations': { fr: 'Animations des graphiques', en: 'Chart Animations' },
  'settings.saveSettings': { fr: 'Enregistrer les paramètres', en: 'Save Settings' },
  'settings.configureSection': { fr: 'Configurez les paramètres de cette section', en: 'Configure settings for this section' },
  'settings.systemInfo': { fr: 'Informations système', en: 'System Information' },
  'settings.savedSuccess': { fr: 'Paramètres enregistrés avec succès !', en: 'Settings saved successfully!' },
  'settings.resetConfirm': { fr: 'Êtes-vous sûr de vouloir réinitialiser tous les paramètres ?', en: 'Are you sure you want to reset all settings?' },
  'settings.resetSuccess': { fr: 'Paramètres réinitialisés !', en: 'Settings reset!' },
  'settings.minutes15': { fr: '15 minutes', en: '15 minutes' },
  'settings.minutes30': { fr: '30 minutes', en: '30 minutes' },
  'settings.hour1': { fr: '1 heure', en: '1 hour' },
  'settings.hours2': { fr: '2 heures', en: '2 hours' },
  'settings.hours4': { fr: '4 heures', en: '4 hours' },

  // ── Team Management ──
  'team.title': { fr: 'Gestion d\'équipe', en: 'Team Management' },
  'team.subtitle': { fr: 'Gérez les membres de votre équipe et leurs permissions', en: 'Manage your team members and their permissions' },
  'team.addMember': { fr: 'Ajouter un membre', en: 'Add Member' },
  'team.totalMembers': { fr: 'Total des membres', en: 'Total Members' },
  'team.activeMembers': { fr: 'Membres actifs', en: 'Active Members' },
  'team.pending': { fr: 'En attente', en: 'Pending' },
  'team.adminTeam': { fr: 'Équipe admin', en: 'Admin Team' },
  'team.addNewMember': { fr: 'Ajouter un nouveau membre', en: 'Add New Team Member' },
  'team.member': { fr: 'Membre', en: 'Member' },
  'team.username': { fr: 'Nom d\'utilisateur', en: 'Username' },
  'team.lastLogin': { fr: 'Dernière connexion', en: 'Last Login' },
  'team.joinedDate': { fr: 'Date d\'inscription', en: 'Joined Date' },
  'team.membersFound': { fr: 'membre(s) trouvé(s)', en: 'member(s) found' },
  'team.you': { fr: 'Vous', en: 'You' },
  'team.never': { fr: 'Jamais', en: 'Never' },
  'team.now': { fr: 'Maintenant', en: 'Now' },
  'team.tempPassword': { fr: 'Mot de passe temporaire', en: 'Temporary Password' },
  'team.tempPasswordNote': { fr: 'Le membre devra changer le mot de passe à la première connexion', en: 'Member will need to change password on first login' },
  'team.financeTeam': { fr: 'Équipe finance', en: 'Finance Team' },
  'team.supportTeam': { fr: 'Équipe support', en: 'Support Team' },
  'team.accountStatus': { fr: 'Statut du compte', en: 'Account Status' },
  'team.connectionStatus': { fr: 'Statut de connexion', en: 'Connection Status' },
  'team.permissionsByRole': { fr: 'Permissions par rôle', en: 'Permissions by Role' },
  'team.fullAccess': { fr: 'Accès complet au système', en: 'Full system access' },
  'team.teamManagement': { fr: 'Gestion des membres de l\'équipe', en: 'Team member management' },
  'team.accountConfig': { fr: 'Configuration du compte', en: 'Account configuration' },
  'team.allPermissions': { fr: 'Toutes les permissions', en: 'All permissions' },
  'team.viewTransactions': { fr: 'Voir les transactions', en: 'View transactions' },
  'team.checkBalances': { fr: 'Vérifier les soldes', en: 'Check balances' },
  'team.generateReports': { fr: 'Générer des rapports', en: 'Generate reports' },
  'team.financialAnalytics': { fr: 'Accès aux analyses financières', en: 'Access financial analytics' },
  'team.manageTransfers': { fr: 'Gérer les transferts', en: 'Manage transfers' },
  'team.customerSupport': { fr: 'Accès au support client', en: 'Access customer support' },
  'team.checkHistory': { fr: 'Consulter l\'historique', en: 'Check history' },
  'team.basicReporting': { fr: 'Rapports basiques', en: 'Basic reporting' },
  'team.passwordReset': { fr: 'Réinitialisation du mot de passe', en: 'Password Reset' },
  'team.newPasswordGenerated': { fr: 'Nouveau mot de passe généré', en: 'New Password Generated' },
  'team.passwordNote': { fr: 'Veuillez fournir ce mot de passe à l\'utilisateur. Il devra le changer à la première connexion.', en: 'Please provide this password to the user. They will need to change it on first login.' },
  'team.copyPassword': { fr: 'Copier le mot de passe', en: 'Copy Password' },
  'team.copyToClipboard': { fr: 'Copier dans le presse-papier', en: 'Copy to clipboard' },
  'team.role': { fr: 'Rôle', en: 'Role' },
  'team.teamMembers': { fr: 'Membres de l\'équipe', en: 'Team Members' },
  'team.noMembersFound': { fr: 'Aucun membre trouvé', en: 'No team members found' },
  'team.startAddMember': { fr: 'Commencez par ajouter un nouveau membre', en: 'Start by adding a new member' },
  'team.deactivateUser': { fr: 'Désactiver l\'utilisateur', en: 'Deactivate User' },
  'team.activateUser': { fr: 'Activer l\'utilisateur', en: 'Activate User' },
  'team.passwordCopied': { fr: 'Mot de passe copié !', en: 'Password copied to clipboard!' },
  'team.fillRequiredFields': { fr: 'Veuillez remplir tous les champs obligatoires', en: 'Please fill all required fields' },
  'team.memberAdded': { fr: 'Membre ajouté avec succès', en: 'Member added successfully' },
  'team.memberUpdated': { fr: 'Membre mis à jour avec succès', en: 'Member updated successfully' },
  'team.memberDeleted': { fr: 'Membre supprimé avec succès', en: 'Member deleted successfully' },
  'team.confirmDelete': { fr: 'Êtes-vous sûr de vouloir supprimer', en: 'Are you sure you want to delete' },
  'team.confirmDeactivate': { fr: 'Êtes-vous sûr de vouloir désactiver', en: 'Are you sure you want to deactivate' },
  'team.confirmActivate': { fr: 'Êtes-vous sûr de vouloir activer', en: 'Are you sure you want to activate' },
  'team.passwordResetSuccess': { fr: 'Mot de passe réinitialisé avec succès pour', en: 'Password reset successfully for' },
  'team.errorLoading': { fr: 'Erreur lors du chargement des données', en: 'Error loading team data' },
  'team.errorAdding': { fr: 'Erreur lors de l\'ajout du membre', en: 'Error adding member' },
  'team.errorUpdating': { fr: 'Erreur lors de la mise à jour du membre', en: 'Error updating member' },
  'team.errorDeleting': { fr: 'Erreur lors de la suppression du membre', en: 'Error deleting member' },
  'team.errorResetPassword': { fr: 'Erreur lors de la réinitialisation du mot de passe', en: 'Error resetting password' },
  'team.userActivated': { fr: 'Utilisateur activé avec succès', en: 'User activated successfully' },
  'team.userDeactivated': { fr: 'Utilisateur désactivé avec succès', en: 'User deactivated successfully' },
  'team.resetPassword': { fr: 'Réinitialiser le mot de passe', en: 'Reset Password' },
  'status.offline': { fr: 'Hors ligne', en: 'Offline' },

  // ── Tickets ──
  'tickets.title': { fr: 'Tickets de support', en: 'Support Tickets' },
  'tickets.subtitle': { fr: 'Gérez et suivez vos demandes de support', en: 'Manage and track your support requests' },
  'tickets.newTicket': { fr: 'Nouveau ticket', en: 'New Ticket' },
  'tickets.totalTickets': { fr: 'Total des tickets', en: 'Total Tickets' },
  'tickets.open': { fr: 'Ouvert', en: 'Open' },
  'tickets.inProgress': { fr: 'En cours', en: 'In Progress' },
  'tickets.resolved': { fr: 'Résolu', en: 'Resolved' },
  'tickets.urgent': { fr: 'Urgent', en: 'Urgent' },
  'tickets.avgResponse': { fr: 'Temps moyen de réponse', en: 'Avg. Response' },
  'tickets.loading': { fr: 'Chargement des tickets...', en: 'Loading tickets...' },
  'tickets.noTickets': { fr: 'Aucun ticket trouvé', en: 'No tickets found' },
  'tickets.noMatch': { fr: 'Aucun ticket ne correspond à vos filtres actuels', en: 'No tickets match your current filters' },
  'tickets.createTicket': { fr: 'Créer un ticket', en: 'Create Ticket' },
  'tickets.createNew': { fr: 'Créer un nouveau ticket', en: 'Create New Ticket' },
  'tickets.searchTickets': { fr: 'Rechercher des tickets...', en: 'Search tickets...' },
  'tickets.filters': { fr: 'Filtres', en: 'Filters' },
  'tickets.allStatuses': { fr: 'Tous les statuts', en: 'All Statuses' },
  'tickets.allPriorities': { fr: 'Toutes les priorités', en: 'All Priorities' },
  'tickets.allCategories': { fr: 'Toutes les catégories', en: 'All Categories' },
  'tickets.conversation': { fr: 'Conversation', en: 'Conversation' },
  'tickets.messages': { fr: 'message(s)', en: 'message(s)' },
  'tickets.typeMessage': { fr: 'Tapez votre message ici...', en: 'Type your message here...' },
  'tickets.ticketDetails': { fr: 'Détails du ticket', en: 'Ticket Details' },
  'tickets.priority': { fr: 'Priorité', en: 'Priority' },
  'tickets.category': { fr: 'Catégorie', en: 'Category' },
  'tickets.createdBy': { fr: 'Créé par :', en: 'Created by:' },
  'tickets.created': { fr: 'Créé le :', en: 'Created:' },
  'tickets.lastUpdated': { fr: 'Dernière mise à jour :', en: 'Last updated:' },
  'tickets.closed': { fr: 'Fermé le :', en: 'Closed:' },
  'tickets.subject': { fr: 'Sujet', en: 'Subject' },
  'tickets.subjectPlaceholder': { fr: 'Brève description de votre problème...', en: 'Brief description of your issue...' },
  'tickets.descriptionPlaceholder': { fr: 'Veuillez fournir des informations détaillées sur votre problème...', en: 'Please provide detailed information about your issue...' },

  // Tickets — refonte 2026-05-06 (inbox style)
  'tickets.colSubject': { fr: 'Sujet', en: 'Subject' },
  'tickets.colCategory': { fr: 'Catégorie', en: 'Category' },
  'tickets.colPriority': { fr: 'Priorité', en: 'Priority' },
  'tickets.colStatus': { fr: 'Statut', en: 'Status' },
  'tickets.colActivity': { fr: 'Activité', en: 'Activity' },
  'tickets.descriptionLabel': { fr: 'Description', en: 'Description' },
  'tickets.searchPlaceholder': { fr: 'Rechercher par sujet, ID, description...', en: 'Search subject, id, description...' },
  'tickets.empty': { fr: 'Aucun ticket pour le moment.', en: 'No tickets here yet.' },
  'tickets.unreadReplies': { fr: 'Réponses non lues', en: 'Unread replies' },
  'tickets.opened': { fr: 'Ouvert le', en: 'Opened' },
  'tickets.assignedTo': { fr: 'Assigné à', en: 'Assigned to' },
  'tickets.originalDescription': { fr: 'Description initiale', en: 'Original description' },
  'tickets.noReplies': { fr: 'Aucune réponse pour le moment. Notre équipe va vous répondre rapidement.', en: 'No replies yet. Our team will respond shortly.' },
  'tickets.threadClosed': { fr: 'Ce ticket est clos. Ouvrez-en un nouveau pour continuer la conversation.', en: 'This ticket is closed. Open a new ticket to continue the conversation.' },
  'tickets.replyPlaceholder': { fr: 'Ajoutez plus de détails, captures d\'écran ou suivi...', en: 'Add more details, screenshots, or follow-up...' },
  'tickets.sendReply': { fr: 'Envoyer la réponse', en: 'Send reply' },
  'tickets.sending': { fr: 'Envoi...', en: 'Sending...' },
  'tickets.notFound': { fr: 'Ticket introuvable.', en: 'Ticket not found.' },
  'tickets.requiredFields': { fr: 'Email, sujet et description sont obligatoires.', en: 'Email, subject and description are required.' },
  'tickets.emailLabel': { fr: 'Email de contact', en: 'Contact email' },
  'tickets.emailHint': {
    fr: "Nous vous contacterons à cette adresse à chaque changement de statut ou réponse de l'équipe support.",
    en: 'We will contact you at this address on every status change or support team reply.'
  },
  'tickets.invalidEmail': { fr: 'Adresse email invalide.', en: 'Invalid email address.' },
  'tickets.submit': { fr: 'Envoyer le ticket', en: 'Submit ticket' },
  'tickets.submitting': { fr: 'Envoi en cours...', en: 'Submitting...' },
  'tickets.loadingDetail': { fr: 'Chargement du ticket...', en: 'Loading ticket...' },
  // Attachments
  'tickets.attachFiles': { fr: 'Joindre des fichiers', en: 'Attach files' },
  'tickets.attachmentHint': { fr: 'Jusqu\'à 3 fichiers, 2 Mo max chacun', en: 'Up to 3 files, 2MB max each' },
  'tickets.attachmentTooLarge': { fr: 'Fichier trop volumineux (max 2 Mo)', en: 'File too large (max 2MB)' },
  'tickets.attachmentTooMany': { fr: 'Maximum 3 fichiers par message', en: 'Max 3 files per message' },
  'tickets.attachmentRemove': { fr: 'Retirer', en: 'Remove' },
  'tickets.attachmentDownload': { fr: 'Télécharger', en: 'Download' },
  'tickets.attachmentsSection': { fr: 'Pièces jointes', en: 'Attachments' },

  // Tickets — announcement modal
  'ticketsAnnounce.eyebrow': { fr: 'Nouveauté', en: 'New' },
  'ticketsAnnounce.title': { fr: 'Votre espace Tickets de support', en: 'Your Support Tickets space' },
  'ticketsAnnounce.subtitle': {
    fr: "Pour les demandes qui ont besoin d'un suivi de bout en bout",
    en: 'For requests that need end-to-end tracking',
  },
  'ticketsAnnounce.lead': {
    fr: "Vous pouvez désormais ouvrir des tickets directement depuis votre dashboard pour tout ce qui nécessite un suivi : bugs, intégrations API, vérifications KYC, plaintes. Notre équipe support traite vos tickets dans un seul outil — rien ne se perd.",
    en: 'You can now open tickets directly from your dashboard for anything that needs follow-up: bugs, API integrations, KYC checks, complaints. Our support team handles tickets in a single tool — nothing gets lost.',
  },
  'ticketsAnnounce.benefit1': {
    fr: 'Historique permanent et recherchable de chaque échange',
    en: 'Permanent, searchable history of every exchange',
  },
  'ticketsAnnounce.benefit2': {
    fr: "Pièces jointes durables : captures d'écran, PDFs (jusqu'à 2 Mo)",
    en: 'Durable attachments: screenshots, PDFs (up to 2MB)',
  },
  'ticketsAnnounce.benefit3': {
    fr: 'Priorité claire (urgent → faible) et statut visible à tout moment',
    en: 'Clear priority (urgent → low) and status at a glance',
  },
  'ticketsAnnounce.matrixTitle': { fr: 'Quand utiliser quoi', en: 'When to use what' },
  'ticketsAnnounce.channelTickets': { fr: 'Tickets', en: 'Tickets' },
  'ticketsAnnounce.channelTicketsDesc': {
    fr: 'Bugs, intégration API, KYC, plaintes, demandes traçables',
    en: 'Bugs, API integration, KYC, complaints, anything traceable',
  },
  'ticketsAnnounce.channelChat': { fr: 'WhatsApp / Telegram', en: 'WhatsApp / Telegram' },
  'ticketsAnnounce.channelChatDesc': {
    fr: 'Questions rapides, vérification de statut',
    en: 'Quick questions, status checks',
  },
  'ticketsAnnounce.channelTeams': { fr: 'Teams', en: 'Teams' },
  'ticketsAnnounce.channelTeamsDesc': {
    fr: 'Escalades grands comptes',
    en: 'Large-account escalations',
  },
  'ticketsAnnounce.footer': {
    fr: 'Vous retrouvez votre espace dans le menu Gestion → Tickets.',
    en: 'Find your space anytime under Management → Tickets.',
  },
  'ticketsAnnounce.cta': { fr: 'Découvrir mes tickets', en: 'Explore my tickets' },
  'ticketsAnnounce.later': { fr: 'Plus tard', en: 'Later' },

  // ── Withdrawal ──
  'withdrawal.title': { fr: 'Décaissement', en: 'Cashout' },
  'withdrawal.subtitle': { fr: 'Envoyez vos fonds vers un compte mobile money externe', en: 'Send your funds to an external mobile money account' },
  'withdrawal.merchant': { fr: 'Marchand :', en: 'Merchant:' },
  'withdrawal.new': { fr: 'Nouveau décaissement', en: 'New Cashout' },
  'withdrawal.history': { fr: 'Historique', en: 'History' },
  'withdrawal.paymentMethod': { fr: 'Méthode de paiement', en: 'Payment Method' },
  'withdrawal.recipientPhone': { fr: 'Numéro de téléphone du destinataire', en: 'Recipient Phone Number' },
  'withdrawal.reference': { fr: 'Référence', en: 'Reference' },
  'withdrawal.noteOptional': { fr: 'Note (Optionnel)', en: 'Note (Optional)' },
  'withdrawal.clearForm': { fr: 'Effacer le formulaire', en: 'Clear Form' },
  'withdrawal.withdraw': { fr: 'Retirer', en: 'Withdraw' },
  'withdrawal.initiate': { fr: 'Initier le retrait', en: 'Initiate Withdrawal' },
  'withdrawal.processing': { fr: 'Traitement du retrait...', en: 'Processing Withdrawal...' },
  'withdrawal.transactionHistory': { fr: 'Historique des transactions', en: 'Transaction History' },
  'withdrawal.loadingHistory': { fr: 'Chargement de l\'historique...', en: 'Loading history...' },
  'withdrawal.noWithdrawals': { fr: 'Aucun retrait', en: 'No Withdrawals Yet' },
  'withdrawal.startFirst': { fr: 'Commencez par initier votre premier retrait depuis l\'onglet Nouveau retrait', en: 'Start by initiating your first withdrawal from the New Withdrawal tab' },
  'withdrawal.walletBalances': { fr: 'Soldes des portefeuilles', en: 'Wallet Balances' },
  'withdrawal.availableByOperator': { fr: 'Disponible par opérateur', en: 'Available by Operator' },
  'withdrawal.stats': { fr: 'Statistiques de retrait', en: 'Withdrawal Stats' },
  'withdrawal.totalThisMonth': { fr: 'Total ce mois', en: 'Total This Month' },
  'withdrawal.allCurrencies': { fr: 'Toutes les devises', en: 'All currencies' },
  'withdrawal.todayTransactions': { fr: 'Transactions du jour', en: 'Today\'s Transactions' },
  'withdrawal.tips': { fr: 'Conseils', en: 'Tips' },
  'withdrawal.tipCorrectNumber': { fr: 'Assurez-vous que le numéro du destinataire est correct', en: 'Ensure recipient number is correct' },
  'withdrawal.tipKeepReference': { fr: 'Conservez la référence de transaction pour le suivi', en: 'Keep transaction reference for tracking' },
  'withdrawal.tipProcessingTime': { fr: 'Les retraits sont traités en 2-5 minutes', en: 'Withdrawals process within 2-5 minutes' },
  'withdrawal.tipContactSupport': { fr: 'Contactez le support pour les transactions échouées', en: 'Contact support for failed transactions' },
  'withdrawal.initiatedSuccess': { fr: 'Retrait initié avec succès', en: 'Withdrawal initiated successfully' },
  'withdrawal.available': { fr: 'Disponible :', en: 'Available:' },
  'withdrawal.sourceTitle': { fr: 'Source — Mes wallets', en: 'Source — My wallets' },
  'withdrawal.sourceSubtitle': { fr: 'Total disponible', en: 'Total available' },
  'withdrawal.targetTitle': { fr: 'Destination — Mobile money', en: 'Destination — Mobile money' },
  'withdrawal.recipientCard': { fr: 'Destinataire', en: 'Recipient' },
  'withdrawal.configTitle': { fr: 'Configuration du décaissement', en: 'Cashout configuration' },
  'withdrawal.detailsTitle': { fr: 'Détails du décaissement', en: 'Cashout details' },
  'withdrawal.amountAvailable': { fr: 'Disponible', en: 'Available' },
  'withdrawal.insufficientBalance': { fr: 'Solde insuffisant', en: 'Insufficient balance' },
  'withdrawal.phoneNotConfigured': { fr: 'Numéro non configuré', en: 'Phone not configured' },
  'withdrawal.loadingConfig': { fr: 'Chargement de la configuration...', en: 'Loading configuration...' },
  'withdrawal.emailLabel': { fr: 'Email pour OTP *', en: 'Email for OTP *' },
  'withdrawal.emailPlaceholder': { fr: 'votre@email.com', en: 'your@email.com' },
  'withdrawal.phoneMpesa': { fr: 'Numéro M-Pesa', en: 'M-Pesa number' },
  'withdrawal.phoneAirtel': { fr: 'Numéro Airtel', en: 'Airtel number' },
  'withdrawal.phoneOrange': { fr: 'Numéro Orange', en: 'Orange number' },
  'withdrawal.phoneAfrimoney': { fr: 'Numéro Afrimoney', en: 'Afrimoney number' },
  'withdrawal.cancel': { fr: 'Annuler', en: 'Cancel' },
  'withdrawal.saving': { fr: 'Enregistrement...', en: 'Saving...' },
  'withdrawal.save': { fr: 'Enregistrer', en: 'Save' },
  'withdrawal.configRequired': { fr: 'Configuration requise', en: 'Configuration required' },
  'withdrawal.configRequiredDesc': {
    fr: 'Configurez votre email OTP et vos numéros pour commencer.',
    en: 'Set up your OTP email and recipient numbers to get started.'
  },
  'withdrawal.configurePhones': { fr: 'Configurer mes numéros', en: 'Configure my numbers' },
  'withdrawal.cashoutTo': { fr: 'Décaissement vers', en: 'Cashout to' },
  'withdrawal.noPhoneFor': { fr: 'Aucun numéro configuré pour', en: 'No number configured for' },
  'withdrawal.modify': { fr: 'Modifier', en: 'Edit' },
  'withdrawal.configure': { fr: 'Configurer', en: 'Configure' },
  'withdrawal.summaryTitle': { fr: 'Récapitulatif du décaissement', en: 'Cashout summary' },
  'withdrawal.myWallets': { fr: 'Mes wallets', en: 'My wallets' },
  'withdrawal.summaryTotal': { fr: 'Total', en: 'Total' },
  'withdrawal.summaryAmount': { fr: 'Montant', en: 'Amount' },
  'withdrawal.summaryOperator': { fr: 'Opérateur', en: 'Operator' },
  'withdrawal.summaryPhone': { fr: 'Numéro', en: 'Phone' },
  'withdrawal.summaryStatus': { fr: 'Statut', en: 'Status' },
  'withdrawal.summaryReady': { fr: 'Prêt', en: 'Ready' },
  'withdrawal.otpTitle': { fr: 'Vérification OTP', en: 'OTP verification' },
  'withdrawal.otpFor': { fr: 'Décaissement', en: 'Cashout' },
  'withdrawal.otpVia': { fr: 'via', en: 'via' },
  'withdrawal.enterOtp': { fr: 'Entrez le code OTP à 6 chiffres', en: 'Enter the 6-digit OTP code' },
  'withdrawal.resendIn': { fr: 'Renvoyer dans', en: 'Resend in' },
  'withdrawal.resendOtp': { fr: 'Renvoyer le code OTP', en: 'Resend OTP code' },
  'withdrawal.donePending': { fr: "En attente d'approbation", en: 'Pending approval' },
  'withdrawal.doneInitiated': { fr: 'Décaissement initié', en: 'Cashout initiated' },
  'withdrawal.referenceLabel': { fr: 'Référence :', en: 'Reference:' },
  'withdrawal.newCashout': { fr: 'Nouveau décaissement', en: 'New cashout' },
  'withdrawal.sending': { fr: 'Envoi en cours...', en: 'Sending...' },
  'withdrawal.cashoutBtn': { fr: 'Décaisser', en: 'Cash out' },
  'withdrawal.confirming': { fr: 'Confirmation...', en: 'Confirming...' },
  'withdrawal.confirmCashout': { fr: 'Confirmer le décaissement', en: 'Confirm cashout' },
  'withdrawal.configApprovalNotice': {
    fr: "Toute modification est soumise à validation par notre backoffice avant d'être appliquée.",
    en: 'Any change must be approved by our backoffice before it takes effect.'
  },
  'withdrawal.configSubmitted': {
    fr: 'Demande envoyée pour approbation backoffice.',
    en: 'Request submitted for backoffice approval.'
  },
  'withdrawal.configNoChange': { fr: 'Aucun champ modifié.', en: 'No field changed.' },
  'withdrawal.configPendingExists': {
    fr: "Une demande est déjà en attente d'approbation.",
    en: 'A request is already pending approval.'
  },
  'withdrawal.configPendingTitle': {
    fr: "Demande en attente d'approbation",
    en: 'Request pending approval'
  },
  'withdrawal.configPendingDesc': {
    fr: "Le backoffice doit approuver vos changements avant qu'ils ne soient appliqués. Cela peut prendre quelques minutes à quelques heures.",
    en: 'The backoffice must approve your changes before they take effect. This may take a few minutes to a few hours.'
  },
  'withdrawal.configCancelPending': { fr: 'Annuler la demande', en: 'Cancel request' },
  'withdrawal.configPendingCancelled': { fr: 'Demande annulée.', en: 'Request cancelled.' },
  'withdrawal.configRejectedTitle': {
    fr: 'Dernière demande rejetée',
    en: 'Last request was rejected'
  },
  'withdrawal.accessRequired': { fr: 'Accès requis', en: 'Access Required' },
  'withdrawal.accessRequiredDesc': { fr: 'La fonctionnalité de retrait nécessite une autorisation spécifique du marchand. Contactez le support pour activer cette fonctionnalité pour votre compte.', en: 'Withdrawal functionality requires specific merchant authorization. Contact support to enable this feature for your account.' },
  'withdrawal.merchantAccount': { fr: 'Compte marchand', en: 'Merchant Account' },
  'withdrawal.limitedAccess': { fr: 'Accès limité', en: 'Limited Access' },
  'withdrawal.verifying': { fr: 'Vérification des permissions de retrait...', en: 'Verifying withdrawal permissions...' },

  // ── Merchant Account ──
  'merchant.title': { fr: 'Compte marchand', en: 'Merchant Account' },
  'merchant.subtitle': { fr: 'Gérez vos identifiants API et informations commerciales', en: 'Manage your API credentials and business information' },
  'merchant.kycStatus': { fr: 'Statut KYC', en: 'KYC Status' },
  'merchant.accountStatus': { fr: 'Statut du compte', en: 'Account Status' },
  'merchant.completeKyc': { fr: 'Complétez votre vérification KYC', en: 'Complete Your KYC Verification' },
  'merchant.missingDocs': { fr: 'Documents manquants :', en: 'Missing documents:' },
  'merchant.completeKycNote': { fr: 'Veuillez compléter votre profil KYC pour accéder à toutes les fonctionnalités', en: 'Please complete your KYC profile to access all features' },
  'merchant.updateCompanyInfo': { fr: 'Mettre à jour les infos de l\'entreprise', en: 'Update Company Info' },
  'merchant.updateDocs': { fr: 'Mettre à jour les documents', en: 'Update Documents' },
  'merchant.apiCredentials': { fr: 'Identifiants API', en: 'API Credentials' },
  'merchant.apiDesc': { fr: 'Clés d\'authentification pour l\'intégration API PayDRC', en: 'Authentication keys for PayDRC API integration' },
  'merchant.apiBaseUrl': { fr: 'URL de base API', en: 'API Base URL' },
  'merchant.merchantId': { fr: 'ID Marchand', en: 'Merchant ID' },
  'merchant.merchantCode': { fr: 'Code Marchand', en: 'Merchant Code' },
  'merchant.apiRequestExample': { fr: 'Exemple de requête API', en: 'API Request Example' },
  'merchant.copyExample': { fr: 'Copier l\'exemple', en: 'Copy Example' },
  'merchant.securityNotice': { fr: 'Avis de sécurité', en: 'Security Notice' },
  'merchant.securityTip1': { fr: 'Ne partagez jamais votre Merchant Secret dans le code côté client', en: 'Never share your Merchant Secret in client-side code' },
  'merchant.securityTip2': { fr: 'Stockez les identifiants en toute sécurité à l\'aide de variables d\'environnement', en: 'Store credentials securely using environment variables' },
  'merchant.securityTip3': { fr: 'Régénérez immédiatement en cas de compromission', en: 'Regenerate immediately if compromised' },
  'merchant.securityTip4': { fr: 'Utilisez toujours HTTPS pour les appels API', en: 'Always use HTTPS for API calls' },
  'merchant.securityTip5': { fr: 'Implémentez une gestion des erreurs appropriée', en: 'Implement proper error handling' },
  'merchant.callbackUrls': { fr: 'URLs de callback', en: 'Callback URLs' },
  'merchant.callbackDesc': { fr: 'Configurez les URLs de retour pour les notifications de transactions', en: 'Configure return URLs for transaction notifications' },
  'merchant.successUrl': { fr: 'URL de succès', en: 'Success URL' },
  'merchant.failureUrl': { fr: 'URL d\'échec', en: 'Failure URL' },
  'merchant.webhookUrl': { fr: 'URL de notification (Webhook)', en: 'Notification URL (Webhook)' },
  'merchant.saveCallbackUrls': { fr: 'Enregistrer les URLs de callback', en: 'Save Callback URLs' },
  'merchant.savingUrls': { fr: 'Enregistrement des URLs...', en: 'Saving URLs...' },
  'merchant.companyInfo': { fr: 'Informations de l\'entreprise', en: 'Company Information' },
  'merchant.businessName': { fr: 'Nom de l\'entreprise', en: 'Business Name' },
  'merchant.editCompanyInfo': { fr: 'Modifier les informations de l\'entreprise', en: 'Edit Company Information' },
  'merchant.kycDocs': { fr: 'Documents KYC', en: 'KYC Documents' },
  'merchant.taxId': { fr: 'NIF (Numéro d\'Identification Fiscale)', en: 'Tax ID (NIF)' },
  'merchant.businessRegister': { fr: 'RCCM (Registre du Commerce)', en: 'Business Register (RCCM)' },
  'merchant.nationalId': { fr: 'Pièce d\'identité nationale', en: 'National ID' },
  'merchant.legalStructure': { fr: 'Structure juridique de l\'entreprise', en: 'Business Legal Structure' },
  'merchant.editKycDocs': { fr: 'Modifier les documents KYC', en: 'Edit KYC Documents' },
  'merchant.selectLegalStatus': { fr: 'Sélectionnez le statut juridique', en: 'Select Legal Status' },
  'merchant.saveDocs': { fr: 'Enregistrer les documents', en: 'Save Documents' },
  'merchant.primaryContact': { fr: 'Contact principal', en: 'Primary Contact' },
  'merchant.contactPerson': { fr: 'Personne de contact', en: 'Contact Person' },
  'merchant.verified': { fr: 'Vérifié', en: 'Verified' },
  'merchant.pendingReview': { fr: 'En cours de révision', en: 'Pending Review' },
  'merchant.notStarted': { fr: 'Non commencé', en: 'Not Started' },
  'merchant.noAccountFound': { fr: 'Aucun compte marchand trouvé', en: 'No Merchant Account Found' },
  'merchant.contactAdmin': { fr: 'Veuillez contacter l\'administrateur pour configurer votre compte marchand', en: 'Please contact the administrator to configure your merchant account' },
  'merchant.industrySector': { fr: 'Secteur d\'activité', en: 'Industry Sector' },
  'merchant.confirmRegenerate': { fr: 'Êtes-vous sûr de vouloir régénérer votre clé secrète ? L\'ancienne clé ne fonctionnera plus.', en: 'Are you sure you want to regenerate your secret key? The old key will no longer work.' },
  'merchant.errorRegenerateSecret': { fr: 'Erreur lors de la régénération de la clé secrète', en: 'Error regenerating secret key' },
  'merchant.callbackUrlsUpdated': { fr: 'URLs de callback mises à jour avec succès !', en: 'Callback URLs updated successfully!' },
  'merchant.errorUpdatingUrls': { fr: 'Erreur lors de la mise à jour des URLs', en: 'Error updating URLs' },
  'merchant.kycUpdated': { fr: 'Informations KYC mises à jour avec succès !', en: 'KYC information updated successfully!' },
  'merchant.errorUpdatingKyc': { fr: 'Erreur lors de la mise à jour des informations KYC', en: 'Error updating KYC information' },
  'merchant.taxIdNumber': { fr: 'Numéro d\'Identification Fiscale', en: 'Tax Identification Number' },
  'merchant.commercialRegistry': { fr: 'Numéro du Registre du Commerce', en: 'Commercial Registry Number' },
  'merchant.nationalIdentification': { fr: 'Identification nationale', en: 'National Identification' },
  'merchant.updateDocuments': { fr: 'Mettre à jour les documents', en: 'Update Documents' },
  'merchant.requiredBusinessVerification': { fr: 'Requis pour la vérification de l\'entreprise', en: 'Required for business verification' },
  'merchant.requiredBusinessRegistration': { fr: 'Requis pour l\'enregistrement de l\'entreprise', en: 'Required for business registration' },
  'merchant.requiredIdentityVerification': { fr: 'Requis pour la vérification d\'identité', en: 'Required for identity verification' },
  'merchant.selectLegalStructure': { fr: 'Sélectionnez la structure juridique de votre entreprise', en: 'Select your business legal structure' },
  'merchant.emailAddress': { fr: 'Adresse email', en: 'Email Address' },
  'merchant.legalStatus': { fr: 'Statut juridique', en: 'Legal Status' },
  'merchant.enterTaxId': { fr: 'Entrez votre Numéro d\'Identification Fiscale', en: 'Enter your Tax Identification Number' },
  'merchant.enterRccm': { fr: 'Entrez votre Numéro du Registre du Commerce', en: 'Enter your Commercial Registry Number' },
  'merchant.enterIdnat': { fr: 'Entrez votre Identification Nationale', en: 'Enter your National Identification' },

  // ── Security PIN ──
  'pin.setupTitle': { fr: 'Définissez votre code PIN de sécurité', en: 'Set Your Security PIN' },
  'pin.setupDesc': { fr: 'Pour sécuriser votre portefeuille et vos transactions, vous devez créer un code PIN à 4 chiffres', en: 'To secure your wallet and transactions, you\'ll need to create a 4-digit PIN' },
  'pin.requiredNote': { fr: 'Ce code PIN sera requis pour tous les transferts et actions sensibles', en: 'This PIN will be required for all transfers and sensitive actions' },
  'pin.setNew': { fr: 'Définir un nouveau PIN', en: 'Set New PIN' },
  'pin.forgotReset': { fr: 'PIN oublié ? Réinitialisez-le', en: 'Forgot PIN? Reset it' },
  'pin.create': { fr: 'Créer le PIN', en: 'Create PIN' },
  'pin.confirm': { fr: 'Confirmer le PIN', en: 'Confirm PIN' },
  'pin.enterPin': { fr: 'Entrez un code PIN à 4 chiffres', en: 'Enter a 4-digit PIN to secure your account' },
  'pin.confirmPin': { fr: 'Veuillez confirmer votre PIN', en: 'Please confirm your PIN' },
  'pin.enter4digit': { fr: 'Entrez un PIN à 4 chiffres', en: 'Enter 4-digit PIN' },
  'pin.confirm4digit': { fr: 'Confirmez le PIN à 4 chiffres', en: 'Confirm 4-digit PIN' },
  'pin.chooseRemember': { fr: 'Choisissez un PIN dont vous pouvez vous souvenir', en: 'Choose a PIN you can remember' },
  'pin.reenterSame': { fr: 'Entrez à nouveau le même PIN', en: 'Re-enter the same PIN' },
  'pin.encrypted': { fr: 'Votre PIN est chiffré de manière sécurisée', en: 'Your PIN is securely encrypted' },
  'pin.forgot': { fr: 'PIN oublié ?', en: 'Forgot your PIN?' },

  // ── PIN Reset ──
  'pinReset.title': { fr: 'Réinitialiser le code PIN de sécurité', en: 'Reset Security PIN' },
  'pinReset.desc': { fr: 'Entrez votre email ou numéro de téléphone pour recevoir un code de réinitialisation', en: 'Enter your email or phone number to receive a reset code' },
  'pinReset.emailAddress': { fr: 'Adresse email', en: 'Email Address' },
  'pinReset.or': { fr: 'OU', en: 'OR' },
  'pinReset.phoneNumber': { fr: 'Numéro de téléphone', en: 'Phone Number' },
  'pinReset.sendCode': { fr: 'Envoyer le code de réinitialisation', en: 'Send Reset Code' },
  'pinReset.sending': { fr: 'Envoi du code...', en: 'Sending Reset Code...' },
  'pinReset.setNewTitle': { fr: 'Définir un nouveau PIN', en: 'Set New PIN' },
  'pinReset.setNewDesc': { fr: 'Entrez le code de vérification et votre nouveau code PIN à 4 chiffres', en: 'Enter the verification code and your new 4-digit PIN' },
  'pinReset.verificationCode': { fr: 'Code de vérification', en: 'Verification Code' },
  'pinReset.enterCode': { fr: 'Entrez le code reçu par email/SMS', en: 'Enter code from email/SMS' },
  'pinReset.newPin': { fr: 'Nouveau PIN à 4 chiffres', en: 'New 4-digit PIN' },
  'pinReset.confirmNewPin': { fr: 'Confirmer le nouveau PIN', en: 'Confirm New PIN' },
  'pinReset.resetPin': { fr: 'Réinitialiser le PIN', en: 'Reset PIN' },
  'pinReset.resetting': { fr: 'Réinitialisation du PIN...', en: 'Resetting PIN...' },
  'pinReset.backToRequest': { fr: 'Retour à la demande', en: 'Back to Request' },
  'pinReset.successTitle': { fr: 'PIN réinitialisé avec succès !', en: 'PIN Reset Successful!' },
  'pinReset.successDesc': { fr: 'Votre code PIN de sécurité a été réinitialisé avec succès. Vous pouvez maintenant utiliser votre nouveau PIN pour les transactions.', en: 'Your security PIN has been reset successfully. You can now use your new PIN for transactions.' },
  'pinReset.redirecting': { fr: 'Vous serez redirigé automatiquement...', en: 'You will be redirected automatically...' },

  // ── PWA Installer ──
  'pwa.installed': { fr: 'MOKO Afrika installé !', en: 'MOKO Afrika installed!' },
  'pwa.installedDesc': { fr: 'L\'application est maintenant disponible sur votre écran d\'accueil', en: 'The application is now available on your home screen' },
  'pwa.alreadyInstalled': { fr: 'Application installée', en: 'Application Installed' },
  'pwa.alreadyInstalledDesc': { fr: 'MOKO Afrika est installé sur votre appareil', en: 'MOKO Afrika is installed on your device' },
  'pwa.installApp': { fr: 'Installer l\'application', en: 'Install Application' },
  'pwa.installAppDesc': { fr: 'Accédez à MOKO Afrika depuis votre écran d\'accueil', en: 'Access MOKO Afrika from your home screen' },
  'pwa.install': { fr: 'Installer', en: 'Install' },
  'pwa.notifTest': { fr: 'Test de notification', en: 'Notification test' },
  'pwa.notifEnabled': { fr: 'Les notifications sont maintenant activées pour MOKO Afrika', en: 'Notifications are now enabled for MOKO Afrika' },

  // ── Header / Notifications ──
  'header.notifications': { fr: 'Notifications', en: 'Notifications' },
  'header.unread': { fr: 'non lu(s)', en: 'unread' },
  'header.markAllRead': { fr: 'Tout marquer comme lu', en: 'Mark all as read' },
  'header.clearAll': { fr: 'Effacer toutes les notifications', en: 'Clear all notifications' },
  'header.noNotifications': { fr: 'Aucune notification pour le moment', en: 'No notifications at the moment' },
  'header.notifAppearHere': { fr: 'Les notifications de transfert apparaîtront ici', en: 'Transfer notifications will appear here' },
  'header.new': { fr: 'Nouveau', en: 'New' },
  'header.justNow': { fr: 'À l\'instant', en: 'Just now' },
  'header.agoMin': { fr: 'Il y a {n} min', en: '{n} min ago' },
  'header.agoHours': { fr: 'Il y a {n} h', en: '{n} h ago' },
  'header.agoDays': { fr: 'Il y a {n} j', en: '{n} d ago' },
  'header.unknownDate': { fr: 'Date inconnue', en: 'Unknown date' },
  'header.switchDark': { fr: 'Passer au thème sombre', en: 'Switch to dark theme' },
  'header.switchLight': { fr: 'Passer au thème clair', en: 'Switch to light theme' },

  // ── Sidebar ──
  'sidebar.expand': { fr: 'Développer', en: 'Expand' },
  'sidebar.collapse': { fr: 'Réduire', en: 'Collapse' },
  'sidebar.noMenu': { fr: 'Aucun menu disponible', en: 'No menu available' },
  'sidebar.section.overview': { fr: 'APERÇU', en: 'OVERVIEW' },
  'sidebar.section.wallets': { fr: 'PORTEFEUILLES', en: 'WALLETS' },
  'sidebar.section.cards': { fr: 'CARTES', en: 'CARDS' },
  'sidebar.section.finance': { fr: 'FINANCE', en: 'FINANCE' },
  'sidebar.section.analytics': { fr: 'ANALYTIQUE', en: 'ANALYTICS' },
  'sidebar.section.management': { fr: 'GESTION', en: 'MANAGEMENT' },
  'nav.cardCashout': { fr: 'Décaissement carte', en: 'Card Cashout' },
  'nav.cardSettings': { fr: 'Paramètres carte', en: 'Card Settings' },
  'nav.cashoutSettings': { fr: 'Paramètres de décaissement', en: 'Cashout Settings' },

  // ── Cashout settings (unified for mobile money + card cashout) ──
  'cashoutSettings.title': { fr: 'Paramètres de décaissement', en: 'Cashout Settings' },
  'cashoutSettings.subtitle': {
    fr: 'Email OTP et numéros mobile money pour vos décaissements (mobile money & cartes).',
    en: 'OTP email and mobile money numbers for all your cashouts (mobile money & cards).'
  },
  'cashoutSettings.statusConfigured': { fr: 'Configuré', en: 'Configured' },
  'cashoutSettings.statusNotConfigured': { fr: 'Non configuré', en: 'Not configured' },
  'cashoutSettings.approvalNotice': {
    fr: "Toute modification (email OTP ou numéros) est soumise à validation par notre backoffice avant d'être appliquée.",
    en: 'Any change (OTP email or numbers) must be approved by our backoffice before it takes effect.'
  },
  'cashoutSettings.emailTitle': { fr: 'Email pour vérification OTP', en: 'OTP verification email' },
  'cashoutSettings.emailDesc': {
    fr: 'Un code OTP à 6 chiffres sera envoyé à cet email pour chaque décaissement.',
    en: 'A 6-digit OTP code will be sent to this email for every cashout.'
  },
  'cashoutSettings.phonesTitle': { fr: 'Numéros par opérateur', en: 'Phone numbers by operator' },
  'cashoutSettings.phonesDesc': {
    fr: 'Un seul numéro par opérateur. Tous les décaissements (mobile money et cartes) atterrissent ici.',
    en: 'One number per operator. All cashouts (mobile money and cards) land here.'
  },
  'cashoutSettings.notSet': { fr: 'Non renseigné', en: 'Not set' },
  'cashoutSettings.modify': { fr: 'Modifier la configuration', en: 'Edit configuration' },
  'cashoutSettings.modifyHint': {
    fr: 'Étape 1/2 : ouvre les champs pour modification.',
    en: 'Step 1/2: opens the fields for editing.'
  },
  'cashoutSettings.configure': { fr: 'Configurer', en: 'Configure' },
  'cashoutSettings.editModeTitle': {
    fr: 'Mode édition activé',
    en: 'Edit mode active'
  },
  'cashoutSettings.editModeHint': {
    fr: "Modifiez les champs ci-dessous puis cliquez sur « Soumettre pour validation » en bas pour envoyer votre demande au backoffice.",
    en: 'Modify the fields below then click "Submit for review" at the bottom to send your request to the backoffice.'
  },
  'cashoutSettings.submitForReview': { fr: 'Soumettre pour validation', en: 'Submit for review' },
  'cashoutSettings.submitting': { fr: 'Envoi...', en: 'Submitting...' },
  'cashoutSettings.disabledPending': {
    fr: "Modification indisponible : une demande est en attente d'approbation.",
    en: 'Editing disabled: a request is pending approval.'
  },
  'cashoutSettings.submitted': {
    fr: 'Demande envoyée pour approbation backoffice.',
    en: 'Change request submitted for backoffice approval.'
  },
  'cashoutSettings.noChange': { fr: 'Aucun champ modifié.', en: 'No field changed.' },
  'cashoutSettings.pendingExists': {
    fr: "Une demande est déjà en attente d'approbation.",
    en: 'A request is already pending approval.'
  },
  'cashoutSettings.pendingTitle': { fr: "Demande en attente d'approbation", en: 'Request pending approval' },
  'cashoutSettings.pendingDesc': {
    fr: "Le backoffice doit approuver vos changements avant qu'ils ne soient appliqués. Cela peut prendre quelques minutes à quelques heures.",
    en: 'The backoffice must approve your changes before they take effect. This typically takes a few minutes to a few hours.'
  },
  'cashoutSettings.cancelPending': { fr: 'Annuler la demande', en: 'Cancel request' },
  'cashoutSettings.pendingCancelled': { fr: 'Demande annulée.', en: 'Request cancelled.' },
  'cashoutSettings.rejectedTitle': { fr: 'Dernière demande rejetée', en: 'Last request rejected' },
  'cashoutSettings.rejectedAdvice': {
    fr: 'Corrigez les valeurs ci-dessous puis soumettez à nouveau.',
    en: 'Fix the values below and submit again.'
  },
  'cashoutSettings.coverageTitle': { fr: 'Couverture opérateurs', en: 'Operator coverage' },
  'cashoutSettings.covered': { fr: 'Configuré', en: 'Configured' },
  'cashoutSettings.empty': { fr: 'Vide', en: 'Empty' },
  'cashoutSettings.securityTitle': { fr: 'Sécurité', en: 'Security' },
  'cashoutSettings.secOTP': { fr: 'OTP requis pour chaque décaissement', en: 'OTP required for every cashout' },
  'cashoutSettings.secApproval': { fr: 'Tout changement passe par notre backoffice', en: 'Every change goes through our backoffice' },
  'cashoutSettings.secNumbersOnly': { fr: 'Décaissements uniquement vers vos numéros enregistrés', en: 'Cashouts only to your registered numbers' },
  'cashoutSettings.secLimits': { fr: 'Limites quotidiennes appliquées automatiquement', en: 'Daily limits enforced automatically' },
  'cashoutSettings.limitsTitle': { fr: 'Limites quotidiennes', en: 'Daily limits' },
  'cashoutSettings.limitsHelp': {
    fr: 'Contactez le support pour ajuster vos limites.',
    en: 'Contact support to adjust your limits.'
  },
  'cashoutSettings.loading': { fr: 'Chargement...', en: 'Loading...' },
  'cashoutSettings.fieldEmail': { fr: 'Email OTP', en: 'OTP email' },

  // ── Withdrawal page links to /cashout-settings ──
  'withdrawal.openCashoutSettings': { fr: 'Paramètres de décaissement', en: 'Cashout settings' },
  'withdrawal.configPendingShort': {
    fr: "Le backoffice doit approuver vos derniers changements avant qu'ils s'appliquent. Vous pouvez continuer à décaisser avec la configuration actuelle.",
    en: 'The backoffice must approve your latest changes before they take effect. You can keep cashing out with the current configuration.'
  },
  'withdrawal.configReviewLink': { fr: 'Voir les détails →', en: 'View details →' },
  'withdrawal.configFixLink': { fr: 'Corriger →', en: 'Fix →' },

  // ── Unified cashout form (Metronic 9 redesign — 2026-06-01) ──
  'withdrawal.stepDetails':       { fr: 'Détails',          en: 'Details' },
  'withdrawal.stepVerify':        { fr: 'Vérification',     en: 'Verify' },
  'withdrawal.stepConfirm':       { fr: 'Confirmation',     en: 'Confirmation' },

  'withdrawal.sourceWallet':      { fr: 'Portefeuille source',                                      en: 'Source wallet' },
  'withdrawal.sourceWalletHint':  { fr: "Choisissez d'où les fonds proviennent",                    en: 'Choose where the funds come from' },
  'withdrawal.channelMobile':     { fr: 'Mobile Money',                                             en: 'Mobile Money' },
  'withdrawal.channelCard':       { fr: 'Carte virtuelle',                                          en: 'Card wallet' },
  'withdrawal.fromMobileWallet':  { fr: 'Depuis vos portefeuilles mobile money',                    en: 'From your mobile money wallets' },
  'withdrawal.fromCardWallet':    { fr: 'Depuis votre solde de carte virtuelle',                    en: 'From your virtual card balance' },
  'withdrawal.notEnabled':        { fr: 'Non activé',                                               en: 'Not enabled' },
  'withdrawal.mobileCashoutLocked': {
    fr: "Le décaissement mobile money doit être activé par le support avant utilisation.",
    en: 'Mobile money cashout must be enabled by support before you can use it.',
  },
  'withdrawal.mobileNoSubscription': {
    fr: "Vous n'êtes pas abonné au canal Mobile Money pour ce marchand.",
    en: 'You are not subscribed to the Mobile Money channel for this merchant.',
  },
  'withdrawal.cardNoSubscription': {
    fr: "Vous n'êtes pas abonné au canal Carte virtuelle pour ce marchand.",
    en: 'You are not subscribed to the Card wallet channel for this merchant.',
  },
  'withdrawal.requestAccess':     { fr: 'Demander l\'activation au support',                         en: 'Request activation from support' },
  'withdrawal.pickOperator':      { fr: 'Choisissez votre opérateur',                               en: 'Pick your operator' },
  'withdrawal.pickCurrency':      { fr: 'Choisissez votre portefeuille carte',                      en: 'Pick your card wallet' },
  'withdrawal.pickMobileCurrency':{ fr: 'Choisissez la devise',                                     en: 'Pick your currency' },
  'withdrawal.cardWallet':        { fr: 'portefeuille',                                             en: 'wallet' },

  'withdrawal.destination':       { fr: 'Destination',                                              en: 'Destination' },
  'withdrawal.destMobileHint':    { fr: 'Les fonds sont envoyés vers le même opérateur',            en: 'Funds are sent to the same operator' },
  'withdrawal.destCardHint':      { fr: 'Choisissez où envoyer les fonds',                          en: 'Pick where to send the funds' },
  'withdrawal.sameOperator':      { fr: 'Même opérateur que la source',                             en: 'Same operator as source' },
  'withdrawal.noPhone':           { fr: 'Non configuré',                                            en: 'Not configured' },

  'withdrawal.amount':            { fr: 'Montant',                                                  en: 'Amount' },
  'withdrawal.amountHint':        { fr: 'Combien voulez-vous envoyer ?',                            en: 'How much do you want to send?' },

  'withdrawal.livePreview':       { fr: 'Récapitulatif',                                            en: 'Summary' },
  'withdrawal.ready':             { fr: 'Prêt',                                                     en: 'Ready' },
  'withdrawal.from':              { fr: 'Depuis',                                                   en: 'From' },
  'withdrawal.to':                { fr: 'Vers',                                                     en: 'To' },
  'withdrawal.fillToContinue':    { fr: 'Remplissez le formulaire pour continuer.',                 en: 'Fill in the form to continue.' },

  'withdrawal.howItWorks':        { fr: 'Comment ça marche',                                        en: 'How it works' },
  'withdrawal.howStep1':          { fr: 'Sélectionnez votre source (Mobile Money ou Carte)',        en: 'Pick your source wallet (Mobile Money or Card)' },
  'withdrawal.howStep2':          { fr: "Confirmez l'opérateur de destination et le destinataire", en: 'Confirm the destination operator and recipient' },
  'withdrawal.howStep3':          { fr: 'Entrez le montant à envoyer et continuez',                 en: 'Enter the amount to send and continue' },
  'withdrawal.howStep4':          { fr: 'Vérifiez avec le code OTP reçu par email',                 en: 'Verify with the OTP sent to your email' },
  'withdrawal.howStep5':          { fr: 'Les fonds sont libérés vers le numéro du destinataire',    en: 'Funds are released to the recipient phone' },

  // ── Roles ──
  'role.merchantAdmin': { fr: 'Admin Marchand', en: 'Merchant Admin' },
  'role.admin': { fr: 'Admin', en: 'Admin' },
  'role.finance': { fr: 'Finance', en: 'Finance' },
  'role.support': { fr: 'Support', en: 'Support' },
  'role.integration': { fr: 'Intégration', en: 'Integration' },
  'role.user': { fr: 'Utilisateur', en: 'User' },
  'team.integrationTeam': { fr: 'Équipe intégration', en: 'Integration Team' },
  'team.manageApiKeys': { fr: 'Gérer les clés API et le secret', en: 'Manage API keys & secret' },
  'team.regenerateSecret': { fr: 'Régénérer le secret marchand', en: 'Regenerate merchant secret' },
  'team.viewEvents': { fr: 'Consulter les événements API', en: 'View API events & logs' },
  'team.readDocumentation': { fr: 'Consulter la documentation', en: 'Read integration documentation' },
  'nav.apiKeys': { fr: 'Clés API', en: 'API Keys' },
  'nav.failureAnalysis': { fr: 'Analyse des échecs', en: 'Failure Analysis' },
  'nav.lookup': { fr: 'Vérifier une transaction', en: 'Transaction Lookup' },
  'nav.events': { fr: 'Événements', en: 'Events' },
  'nav.documentation': { fr: 'Documentation', en: 'Documentation' },
  'nav.support': { fr: 'Support', en: 'Support' },
  'sidebar.section.developers': { fr: 'DÉVELOPPEURS', en: 'DEVELOPERS' },
  'sidebar.section.resources': { fr: 'RESSOURCES', en: 'RESOURCES' },

  // ── Countries ──
  'country.select': { fr: 'Sélectionner un pays', en: 'Select country' },
  'country.current': { fr: 'Pays actuel', en: 'Current country' },

  // ── Languages ──
  'language.french': { fr: 'Français', en: 'French' },
  'language.english': { fr: 'Anglais', en: 'English' },
  'language.select': { fr: 'Choisir la langue', en: 'Select language' },

  // ── ChatBot / Moko Assistant ──
  'chat.assistantName': { fr: 'Moko Afrika', en: 'Moko Afrika' },
  'chat.assistantSubtitle': { fr: 'Assistant intelligent', en: 'Smart assistant' },
  'chat.online': { fr: 'En ligne', en: 'Online' },
  'chat.analyzing': { fr: 'Analyse en cours...', en: 'Analyzing...' },
  'chat.placeholder': { fr: 'Posez votre question...', en: 'Ask your question...' },
  'chat.poweredBy': { fr: 'Assistant FreshPay', en: 'FreshPay Assistant' },
  'chat.newConversation': { fr: 'Nouvelle conversation', en: 'New conversation' },
  'chat.close': { fr: 'Fermer', en: 'Close' },
  'chat.quickActions': { fr: 'Actions rapides', en: 'Quick actions' },
  'chat.suggestions': { fr: 'Suggestions', en: 'Suggestions' },
  'chat.openAssistant': { fr: "Ouvrir l'assistant Moko Afrika", en: 'Open Moko Afrika assistant' },
  'chat.send': { fr: 'Envoyer', en: 'Send' },
  'chat.connectionError': { fr: 'Connexion interrompue', en: 'Connection lost' },
  'chat.connectionErrorDesc': { fr: 'Impossible de joindre le serveur. Verifiez votre connexion internet et reessayez.', en: 'Unable to reach the server. Check your internet connection and try again.' },
  'chat.sessionExpired': { fr: 'Session expirée', en: 'Session expired' },
  'chat.sessionExpiredDesc': { fr: 'Votre session a expiré. Reconnexion en cours...', en: 'Your session has expired. Reconnecting...' },
  'chat.sessionExpiredLogin': { fr: 'Votre session a expiré. Veuillez vous reconnecter.', en: 'Your session has expired. Please log in again.' },
  'chat.reconnecting': { fr: 'Reconnexion...', en: 'Reconnecting...' },
  'chat.contactDirect': { fr: 'Contact direct', en: 'Direct contact' },
  'chat.anotherQuestion': { fr: 'Poser une autre question', en: 'Ask another question' },
  'chat.canIHelp': { fr: 'Puis-je vous aider avec autre chose ?', en: 'Can I help you with anything else?' },
  // Chat welcome
  'chat.welcome': { fr: 'Bienvenue sur', en: 'Welcome to' },
  'chat.welcomeDesc': { fr: 'Je suis votre assistant intelligent, disponible pour vous aider avec vos operations de paiement.', en: "I'm your smart assistant, here to help with your payment operations." },
  'chat.welcomeAction': { fr: 'Selectionnez une action rapide ci-dessous ou posez-moi directement votre question.', en: 'Select a quick action below or ask me your question directly.' },
  // Chat quick actions
  'chat.action.balance': { fr: 'Solde', en: 'Balance' },
  'chat.action.balanceMsg': { fr: 'Consulter mon solde', en: 'Check my balance' },
  'chat.action.transactions': { fr: 'Transactions', en: 'Transactions' },
  'chat.action.transactionsMsg': { fr: 'Rechercher une transaction', en: 'Search for a transaction' },
  'chat.action.network': { fr: 'Réseau', en: 'Network' },
  'chat.action.networkMsg': { fr: 'Statut des opérateurs', en: 'Operator status' },
  'chat.action.withdrawals': { fr: 'Retraits', en: 'Withdrawals' },
  'chat.action.withdrawalsMsg': { fr: 'Informations sur les retraits', en: 'Withdrawal information' },
  'chat.action.transfers': { fr: 'Transferts', en: 'Transfers' },
  'chat.action.transfersMsg': { fr: 'Suivi des transferts', en: 'Transfer tracking' },
  'chat.action.fees': { fr: 'Frais', en: 'Fees' },
  'chat.action.feesMsg': { fr: 'Consulter la grille tarifaire', en: 'View pricing information' },
  'chat.action.recent': { fr: 'Récentes', en: 'Recent' },
  'chat.action.recentMsg': { fr: 'Transactions réussies récentes', en: 'Recent successful transactions' },
  'chat.action.support': { fr: 'Support', en: 'Support' },
  'chat.action.supportMsg': { fr: 'Contacter le support', en: 'Contact support' },
  'chat.action.help': { fr: 'Aide', en: 'Help' },
  'chat.action.helpMsg': { fr: 'Que peux-tu faire ?', en: 'What can you do?' },
  // Chat suggested prompts
  'chat.suggest.balance': { fr: 'Quel est mon solde actuel ?', en: 'What is my current balance?' },
  'chat.suggest.operator': { fr: 'Quel opérateur est le plus performant ?', en: 'Which operator performs best?' },
  'chat.suggest.failed': { fr: "J'ai une transaction en échec", en: 'I have a failed transaction' },
  'chat.suggest.recent': { fr: 'Transactions réussies récentes', en: 'Recent successful transactions' },
  // Chat ticket
  'chat.ticket.title': { fr: 'Ticket de support', en: 'Support ticket' },
  'chat.ticket.needHelp': { fr: "Besoin d'aide supplémentaire ?", en: 'Need more help?' },
  'chat.ticket.desc': { fr: 'Il semble que votre demande nécessite une assistance personnalisée. Souhaitez-vous ouvrir un ticket de support ?', en: 'It seems your request requires personalized assistance. Would you like to open a support ticket?' },
  'chat.ticket.team': { fr: 'Notre équipe traitera votre demande dans les meilleurs délais.', en: 'Our team will process your request as soon as possible.' },
  'chat.ticket.open': { fr: 'Ouvrir un ticket', en: 'Open a ticket' },
  // Smart insights labels
  'chat.insight.dailySummary': { fr: 'Résumé du jour', en: 'Daily Summary' },
  'chat.insight.dailySummaryMsg': { fr: 'Donne-moi un résumé de mes activités du jour', en: 'Give me a summary of today\'s activities' },
  'chat.insight.topUp': { fr: 'Recharger', en: 'Top Up' },
  'chat.insight.topUpMsg': { fr: 'Comment recharger mon solde ?', en: 'How do I top up my balance?' },
  'chat.insight.exportReport': { fr: 'Exporter', en: 'Export' },
  'chat.insight.exportReportMsg': { fr: 'Comment exporter un rapport ?', en: 'How do I export a report?' },
  'chat.insight.failedTx': { fr: 'Échecs récents', en: 'Recent Failures' },
  'chat.insight.failedTxMsg': { fr: 'Montre-moi les transactions échouées récentes', en: 'Show me recent failed transactions' },

  // ── Legal Structures ──
  'legal.sarl': { fr: 'SARL (Société à Responsabilité Limitée)', en: 'SARL (Limited Liability Company)' },
  'legal.sa': { fr: 'SA (Société Anonyme)', en: 'SA (Public Limited Company)' },
  'legal.snc': { fr: 'SNC (Société en Nom Collectif)', en: 'SNC (General Partnership)' },
  'legal.scs': { fr: 'SCS (Société en Commandite Simple)', en: 'SCS (Limited Partnership)' },
  'legal.ei': { fr: 'EI (Entreprise Individuelle)', en: 'EI (Individual Enterprise)' },
  'legal.other': { fr: 'Autre', en: 'Other' },
  // ── Batch fix 2026-07-28 : 83 pre-existing missing keys detected via grep ──
  'account.loading': { fr: 'Chargement du compte...', en: 'Loading account...' },
  'balances.analyzeBalances': { fr: 'Analyser les balances', en: 'Analyze balances' },
  'balances.currencyBreakdown': { fr: 'Répartition par devise', en: 'Currency breakdown' },
  'balances.detailsHelp': { fr: 'Cliquez sur un wallet pour voir les détails', en: 'Click a wallet for details' },
  'balances.shareOfTotal': { fr: 'Part du total', en: 'Share of total' },
  'balances.title': { fr: 'Balances', en: 'Balances' },
  'balances.totalHint': { fr: 'Somme de tous les wallets actifs', en: 'Sum of all active wallets' },
  'cashoutSettings.cancelPending': { fr: 'Annuler la demande en attente', en: 'Cancel pending request' },
  'cashoutSettings.configure': { fr: 'Configurer', en: 'Configure' },
  'cashoutSettings.coverageTitle': { fr: 'Couverture opérateurs', en: 'Operator coverage' },
  'cashoutSettings.editModeHint': { fr: 'Vos modifications passent en revue avant activation.', en: 'Your changes go through review before activation.' },
  'cashoutSettings.emailTitle': { fr: 'Email de notification', en: 'Notification email' },
  'cashoutSettings.loading': { fr: 'Chargement des paramètres...', en: 'Loading settings...' },
  'cashoutSettings.noChange': { fr: 'Aucun changement', en: 'No change' },
  'cashoutSettings.notSet': { fr: 'Non configuré', en: 'Not set' },
  'cashoutSettings.pendingTitle': { fr: 'Demande en attente', en: 'Pending request' },
  'cashoutSettings.phonesTitle': { fr: 'Numéros par opérateur', en: 'Phone numbers per operator' },
  'cashoutSettings.statusConfigured': { fr: 'Configuré', en: 'Configured' },
  'cashoutSettings.submitForReview': { fr: 'Soumettre pour validation', en: 'Submit for review' },
  'cashoutSettings.submitted': { fr: 'Soumis', en: 'Submitted' },
  'cashoutSettings.title': { fr: 'Paramètres de décaissement', en: 'Cashout settings' },
  'chat.action.balance': { fr: 'Voir mon solde', en: 'Show my balance' },
  'chat.assistantName': { fr: 'Assistant Moko', en: 'Moko Assistant' },
  'chat.insight.dailySummary': { fr: 'Récapitulatif du jour', en: 'Daily summary' },
  'chat.suggest.balance': { fr: 'Combien j\'ai dans mon wallet ?', en: 'What\'s my wallet balance?' },
  'chat.ticket.title': { fr: 'Ouvrir un ticket', en: 'Open a ticket' },
  'chat.welcome': { fr: 'Bonjour, comment puis-je vous aider ?', en: 'Hello, how can I help you?' },
  'common.hide': { fr: 'Masquer', en: 'Hide' },
  'common.noData': { fr: 'Aucune donnée', en: 'No data' },
  'country.select': { fr: 'Sélectionner un pays', en: 'Select a country' },
  'dashboard.failureRate': { fr: 'Taux d\'échec', en: 'Failure rate' },
  'dashboard.successRate': { fr: 'Taux de succès', en: 'Success rate' },
  'dashboard.thisMonth': { fr: 'Ce mois-ci', en: 'This month' },
  'header.notifications': { fr: 'Notifications', en: 'Notifications' },
  'language.french': { fr: 'Français', en: 'French' },
  'merchant.errorLoadingProfile': { fr: 'Erreur de chargement du profil.', en: 'Failed to load profile.' },
  'merchant.newSecretGenerated': { fr: 'Nouveau secret généré', en: 'New secret generated' },
  'merchant.title': { fr: 'Marchand', en: 'Merchant' },
  'nav.dashboard': { fr: 'Tableau de bord', en: 'Dashboard' },
  'pin.setupTitle': { fr: 'Configurer votre PIN', en: 'Set up your PIN' },
  'pinReset.title': { fr: 'Réinitialiser le PIN', en: 'Reset PIN' },
  'profile.loading': { fr: 'Chargement du profil...', en: 'Loading profile...' },
  'pwa.installed': { fr: 'Application installée', en: 'App installed' },
  'role.merchantAdmin': { fr: 'Administrateur marchand', en: 'Merchant admin' },
  'settings.title': { fr: 'Paramètres', en: 'Settings' },
  'sidebar.expand': { fr: 'Étendre', en: 'Expand' },
  'team.title': { fr: 'Mon équipe', en: 'My team' },
  'tickets.attachFiles': { fr: 'Joindre des fichiers', en: 'Attach files' },
  'tickets.colSubject': { fr: 'Sujet', en: 'Subject' },
  'tickets.invalidEmail': { fr: 'Email invalide', en: 'Invalid email' },
  'tickets.title': { fr: 'Tickets', en: 'Tickets' },
  'ticketsAnnounce.benefit1': { fr: 'Réponses plus rapides et traçables', en: 'Faster and traceable answers' },
  'ticketsAnnounce.benefit2': { fr: 'Un seul canal pour tous vos échanges', en: 'Single channel for all your queries' },
  'ticketsAnnounce.benefit3': { fr: 'Historique complet et exportable', en: 'Full and exportable history' },
  'ticketsAnnounce.channelChat': { fr: 'Chat en direct', en: 'Live chat' },
  'ticketsAnnounce.channelTeams': { fr: 'Équipe support dédiée', en: 'Dedicated support team' },
  'ticketsAnnounce.cta': { fr: 'Ouvrir mon premier ticket', en: 'Open my first ticket' },
  'ticketsAnnounce.eyebrow': { fr: 'NOUVEAU', en: 'NEW' },
  'ticketsAnnounce.footer': { fr: 'Disponible 24/7 depuis votre espace marchand.', en: 'Available 24/7 from your merchant portal.' },
  'ticketsAnnounce.lead': { fr: 'Un espace unique pour vos demandes de support.', en: 'One single space for your support requests.' },
  'ticketsAnnounce.matrixTitle': { fr: 'Comparaison des canaux', en: 'Channel comparison' },
  'transfers.availableTypes': { fr: 'Types de transferts disponibles', en: 'Available transfer types' },
  'transfers.cardToMobile.configuration': { fr: 'Configuration Carte → Mobile', en: 'Card → Mobile configuration' },
  'transfers.cardToMobile.loadCardError': { fr: 'Impossible de charger le wallet carte.', en: 'Failed to load card wallet.' },
  'transfers.cardToMobile.securityHint': { fr: 'Vérifiez le numéro destinataire avant de valider.', en: 'Double-check the recipient number before confirming.' },
  'transfers.cardToMobile.sourceCard': { fr: 'Wallet carte source', en: 'Source card wallet' },
  'transfers.cardToMobile.withdrawalHint': { fr: 'Les fonds arrivent chez le bénéficiaire sous quelques minutes.', en: 'Funds reach the recipient within minutes.' },
  'transfers.title': { fr: 'Transferts', en: 'Transfers' },
  'withdrawal.accessRequired': { fr: 'Accès requis', en: 'Access required' },
  'withdrawal.amount': { fr: 'Montant', en: 'Amount' },
  'withdrawal.cardNoSubscription': { fr: 'Décaissement carte non activé.', en: 'Card cashout not enabled.' },
  'withdrawal.configPendingTitle': { fr: 'Configuration en attente de validation', en: 'Configuration pending review' },
  'withdrawal.configReviewLink': { fr: 'Voir la demande en cours', en: 'View pending request' },
  'withdrawal.configurePhones': { fr: 'Configurer les numéros', en: 'Configure phone numbers' },
  'withdrawal.destination': { fr: 'Destination', en: 'Destination' },
  'withdrawal.howItWorks': { fr: 'Comment ça marche', en: 'How it works' },
  'withdrawal.livePreview': { fr: 'Aperçu en direct', en: 'Live preview' },
  'withdrawal.mobileNoSubscription': { fr: 'Décaissement mobile non activé.', en: 'Mobile cashout not enabled.' },
  'withdrawal.openCashoutSettings': { fr: 'Ouvrir les paramètres de décaissement', en: 'Open cashout settings' },
  'withdrawal.requestAccess': { fr: 'Demander l\'accès', en: 'Request access' },
  'withdrawal.sourceWallet': { fr: 'Wallet source', en: 'Source wallet' },
  'withdrawal.stepDetails': { fr: 'Détails de la transaction', en: 'Transaction details' },
  'withdrawal.title': { fr: 'Décaissement', en: 'Cashout' },

};

interface LocalizationContextType {
  language: Language;
  country: Country;
  countryConfig: CountryConfig;
  setLanguage: (lang: Language) => void;
  setCountry: (country: Country) => void;
  t: (key: string) => string;
  formatCurrency: (amount: number, currency?: string) => string;
  formatPhoneNumber: (phone: string) => string;
  formatDate: (dateString: string, options?: Intl.DateTimeFormatOptions) => string; // AJOUTER CETTE LIGNE
  getAvailableCountries: () => CountryConfig[];
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

export const LocalizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('fr');
  const [country, setCountry] = useState<Country>('CD');

  useEffect(() => {
    // Detect country from URL, localStorage, or browser
    const detectCountry = (): Country => {
      // Check URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const urlCountry = urlParams.get('country') as Country;
      if (urlCountry && countryConfigs[urlCountry]) {
        return urlCountry;
      }

      // Check localStorage
      const savedCountry = localStorage.getItem('moko_country') as Country;
      if (savedCountry && countryConfigs[savedCountry]) {
        return savedCountry;
      }

      // Check subdomain (e.g., cd.mokoafrika.com)
      const subdomain = window.location.hostname.split('.')[0].toUpperCase() as Country;
      if (countryConfigs[subdomain]) {
        return subdomain;
      }

      // Default to CD (RDC)
      return 'CD';
    };

    // Detect language from browser or localStorage
    const detectLanguage = (): Language => {
      const savedLang = localStorage.getItem('moko_language') as Language;
      if (savedLang && ['fr', 'en'].includes(savedLang)) {
        return savedLang;
      }

      const browserLang = navigator.language.split('-')[0];
      return browserLang === 'fr' ? 'fr' : 'en';
    };

    setCountry(detectCountry());
    setLanguage(detectLanguage());
  }, []);

  const countryConfig = countryConfigs[country];

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('moko_language', lang);
  };

  const handleSetCountry = (newCountry: Country) => {
    setCountry(newCountry);
    localStorage.setItem('moko_country', newCountry);
  };

  // LocalizationContext.tsx
// Ajoutez cette fonction dans le LocalizationProvider

const formatDate = (dateString: string, options?: Intl.DateTimeFormatOptions): string => {
  try {
    const date = new Date(dateString);
    
    // Options par défaut
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    
    // Fusionner avec les options personnalisées
    const formatOptions = { ...defaultOptions, ...options };
    
    const locale = language === 'fr' ? 'fr-FR' : 'en-US';
    
    return new Intl.DateTimeFormat(locale, formatOptions).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; // Retourner la chaîne originale en cas d'erreur
  }
};

  const t = (key: string): string => {
    const translation = translations[key];
    if (!translation) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }
    return translation[language] || translation.fr || key;
  };

  const formatCurrency = (amount: number, currency?: string): string => {
    const curr = currency || countryConfig.primaryCurrency;
    
    const formatters: Record<string, Intl.NumberFormat> = {
      'CDF': new Intl.NumberFormat(language === 'fr' ? 'fr-CD' : 'en-CD', {
        style: 'currency',
        currency: 'CDF',
        minimumFractionDigits: 0
      }),
      'XOF': new Intl.NumberFormat(language === 'fr' ? 'fr-CI' : 'en-CI', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }),
      'USD': new Intl.NumberFormat(language === 'fr' ? 'fr-US' : 'en-US', {
        style: 'currency',
        currency: 'USD'
      }),
      'EUR': new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-FR', {
        style: 'currency',
        currency: 'EUR'
      }),
      'GHS': new Intl.NumberFormat(language === 'fr' ? 'fr-GH' : 'en-GH', {
        style: 'currency',
        currency: 'GHS'
      }),
      'KES': new Intl.NumberFormat(language === 'fr' ? 'fr-KE' : 'en-KE', {
        style: 'currency',
        currency: 'KES'
      }),
      'UGX': new Intl.NumberFormat(language === 'fr' ? 'fr-UG' : 'en-UG', {
        style: 'currency',
        currency: 'UGX',
        minimumFractionDigits: 0
      }),
      'TZS': new Intl.NumberFormat(language === 'fr' ? 'fr-TZ' : 'en-TZ', {
        style: 'currency',
        currency: 'TZS',
        minimumFractionDigits: 0
      }),
      'RWF': new Intl.NumberFormat(language === 'fr' ? 'fr-RW' : 'en-RW', {
        style: 'currency',
        currency: 'RWF',
        minimumFractionDigits: 0
      })
    };

    const formatter = formatters[curr];
    if (formatter) {
      return formatter.format(amount);
    }

    // Fallback
    return `${amount.toLocaleString()} ${curr}`;
  };

  const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    
    // Apply country-specific formatting
    const format = countryConfig.phoneFormat;
    let formatted = format;
    
    for (let i = 0; i < digits.length; i++) {
      formatted = formatted.replace('X', digits[i]);
    }
    
    return formatted.replace(/X/g, '');
  };

  const getAvailableCountries = (): CountryConfig[] => {
    return Object.values(countryConfigs);
  };

  return (
    <LocalizationContext.Provider value={{
      language,
      country,
      countryConfig,
      setLanguage: handleSetLanguage,
      setCountry: handleSetCountry,
      t,
      formatCurrency,
      formatPhoneNumber,
      formatDate,
      getAvailableCountries
    }}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (context === undefined) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
};